/**
 * Extractor de Infraestructuras PTEL v4.1
 * 
 * CHANGELOG v4.1 (18-Ene-2026) - BUG-PARSE-001 Multirow Expansion:
 * - FIX CRÍTICO: Tablas con formato "pseudo-multirow" (ej: Colomera) ahora se expanden correctamente
 * - Nueva función extractParagraphsFromCell() extrae párrafos individuales de celdas ODT
 * - Nueva función getRowCellsWithParagraphs() preserva estructura de párrafos
 * - Nueva función shouldExpandRow() detecta filas que necesitan expansión
 * - Nueva función expandMultiParagraphRow() alinea párrafos por índice
 * - Colomera: ~30 infraestructuras extraídas correctamente (antes: 1 concatenada)
 * - Configuración MULTIROW_CONFIG para umbrales de detección
 *
 * CHANGELOG v4.0 (09-Ene-2026) - Sprint 1 Circuit Breakers:
 * - FIX CRÍTICO: detectMunicipality() ya no devuelve "Ayuntamiento" ni "Ayto"
 * - NUEVO: extractMunicipalityFromContent() busca municipio en el contenido del documento
 * - NUEVO: isHydrographyTableValidator() excluye tablas de ríos/arroyos (falsos positivos)
 * - NUEVO: validateMunicipality() verifica contra lista negra de términos genéricos
 * - Prioridad municipio: parámetro > contenido > filename
 * - Soporte nombres multi-palabra: "La Guardia de Jaén", "El Cuervo de Sevilla"
 * - Expansión abreviaturas: "d" → "de", "Jaen" → "Jaén"
 *
 * CHANGELOG v3.9 (04-Ene-2026):
 * - FIX: Bug crítico - Tabla4 Guadix (longitudes hidrográficas) extraída como infraestructuras
 * - Nueva función isLengthMeasurementHeader() para excluir headers de medición
 * - Nueva función tableHasValidUTMData() para validar datos UTM en tabla
 * - "Longitud dentro del Municipio (m)" ya NO se detecta como coordenada X
 * - Requiere al menos 1 fila con coordenadas UTM válidas para aceptar tabla
 *
 * CHANGELOG v3.8 (16-Dic-2025):
 * - EXT-05: Detección de coordenadas concatenadas en misma celda
 * - Nueva función splitConcatenatedCoordinates() para separar "524538 4229920"
 * - Soporta separadores: espacio, punto y coma, slash, tabulador
 * - Distingue separadores de miles de coordenadas concatenadas
 *
 * CHANGELOG v3.7 (16-Dic-2025):
 * - EXT-04: Detección y extracción de tablas anidadas en ODT
 * - Nueva función findNestedTables() para detectar tablas dentro de celdas
 * - Nueva función extractSectionContext() para heredar contexto de sección padre
 * - Procesa tablas anidadas recursivamente manteniendo jerarquía
 * - Recupera ~10-15 infraestructuras adicionales de secciones con sub-tablas
 *
 * CHANGELOG v3.6 (16-Dic-2025):
 * - EXT-03: Detección de sub-headers multi-fila en XLSX/ODT con fusiones complejas
 * - Nueva función detectMultiRowHeaderCount() para tablas con 3+ filas de headers
 * - Analiza títulos de sección, headers principales y sub-headers de coordenadas
 * - Maneja tablas con filas de título fusionado seguidas de headers y sub-headers
 *
 * CHANGELOG v3.5 (16-Dic-2025):
 * - EXT-02: Detección de columnas __EMPTY en XLSX con celdas fusionadas
 * - Nueva función findEmptyColumnsForCoords() para headers fusionados
 * - Cuando "Coordenadas" está fusionado, busca X/Y en columnas __EMPTY adyacentes
 * - Recupera ~15 coordenadas adicionales en XLSX con formato problemático
 *
 * CHANGELOG v3.4 (16-Dic-2025):
 * - EXT-01: Buscar Y en columnas adyacentes cuando X existe pero Y falta
 * - Nueva función findAdjacentCoordinate(cells, refColIdx, type)
 * - Aplicado en extractFromODT y extractFromSpreadsheet
 * - Recupera ~30 coordenadas adicionales en documentos con Y desplazada
 * 
 * CHANGELOG v3.3 (30-Nov-2025):
 * - FIX: Integrada desconcatenación en extractFromODT y extractFromSpreadsheet
 * - FIX: Corrige texto concatenado como "FARMACIAM.ªCarmen" → "FARMACIA M.ª Carmen"
 * 
 * CHANGELOG v3.2 (28-Nov-2025):
 * - FIX: Mejorada detección de sub-headers con "Longitud/Latitud" (tablas Hornos)
 * - FIX: Eliminado falso positivo de "y" española en patrones coordY
 * - FIX: Ampliado límite de celdas no vacías para sub-headers de 3 a 4
 * 
 * Estrategia ESCALONADA para identificar infraestructuras:
 * 1. Detección por ESTRUCTURA de tabla (columnas Nombre/Dirección/Coordenadas)
 * 2. Lista blanca de SECCIONES conocidas PTEL
 * 3. Detección por CONTENIDO (patrones de infraestructura)
 * 
 * Extrae TANTO infraestructuras con coordenadas COMO sin coordenadas
 * para posterior geocodificación.
 * 
 * @module lib/documentExtractor
 */
import JSZip from 'jszip';
// XLSX se carga dinámicamente para reducir bundle inicial
import type { ExtractedInfrastructure, ExtractionStats, DocumentMetadata, AddressType, TableIngestionBalance, IngestionBalance } from '../types/processing';
import { deconcatenateText } from './textDeconcatenator';
// M01: Reparación de texto fragmentado por spans ODT
import { normalizeSpanFragments } from './spanFragmentNormalizer';
// M02: Fallback de extracción por texto libre
import { extractFromODTFreeText } from './odtFreeTextFallback';
// E2: Detector de datum ED50 para documentos pre-2007
import { detectarED50, tieneIndiciosED50 } from './ed50Detector';
import type { ED50Detection } from '../types/processing';
// EXT-06/EXT-07: Extractor de coordenadas en texto libre
import { extractCoordinatesFromField } from './freeTextCoordExtractor';
// FMT-03: Pipeline de filtros ordenados
import { preProcessCoordinateValue as pipelinePreProcess } from '../pipeline/integration';
import { type DocumentFormat } from '../utils/formatDetector';
import { ANDALUCIA_BOUNDS_UTM30, toUtmInZone } from '../utils/projections';
// FORK-A (A.1, β): motor único de coordenada — mismo parser vivo de la edición P2.
import { parseCoordinateInput } from '../utils/coordinateParser';
// Sprint 1: Circuit breaker para validación pre-geocodificación
import { 
  extractMunicipalityFromContent, 
  isHydrographyTable as isHydrographyTableValidator,
  validateMunicipality,
} from './ExtractionValidator';

// FIX INE-01: Obtener código INE del municipio detectado
import { getCodigoINE, getMunicipioPorCodigo, buscarMunicipio } from './codigosINEUnificado';
// SIGRE: expresión única de las siglas educativas punteadas (DOP-SIGRE-FORMA=b)
import { tieneSiglaEducativaPunteada } from './siglasEducativas';
import { aCajaDireccion, aCajaRAE, conMunicipioAyuntamiento, difierenSinCaja, reponerLetraPorCatalogo } from './nombreCaja';
// AFOROP1: el bloque «AFORO…» recortado del nombre se captura con el parser F7B.06
import { extractAforo } from '../services/parser/AforoParser';
import { extraerFichaAlbergue, type FichaAlbergue } from './albergue/fichaAlbergue';

// ============================================================================
// AFOROP1 · recorte del bloque «AFORO…» del nombre, con captura del aforo
// ============================================================================

/** Bloque «AFORO…» embebido en la celda de nombre (EXT-07b): desde el espacio previo hasta el final. */
const BLOQUE_AFORO = /\s+AFORO[\s:_].*/i;

/** Párrafo de celda que empieza por «AFORO» (AFOROP2): se une al párrafo anterior de su casilla. */
const PARRAFO_AFORO = /^AFORO[\s:_]/i;

export interface NombreSinAforo {
  /** Nombre ya sin el bloque AFORO, en caja RAE (y «Ayuntamiento de <municipio>» si procede: AYTOMUN) */
  nombreClean: string;
  /** Nombre sin el bloque AFORO en caja RAE, antes del sufijo de AYTOMUN (para `difierenSinCaja`: el municipio añadido no es pérdida) */
  nombreComparable: string;
  /** Nombre crudo sin el bloque AFORO y sin caja (para `difierenSinCaja`: el aforo capturado no es pérdida) */
  nombreRawSinAforo: string;
  /** Aforo capturado del bloque (undefined si no hay bloque o viene vacío: «AFORO:_____») */
  aforoDeclarado?: number;
  aforoUnidad?: string;
  aforoFuente?: 'nombre';
  aforoConfianza?: string;
}

/**
 * AFOROP1 (DOP-1 b′, DOP-2 a): separa el bloque «AFORO…» del nombre y lo pasa a `extractAforo`
 * (con «_» → espacio: el prefijo del parser no admite `AFORO:_70`). Medido sobre el corpus
 * (24-sep-2026): 101 filas con bloque, 68 con número → 68/68 capturadas; 33 vacías → sin aforo.
 */
export function recortarAforoDelNombre(nombre: string, nombreRaw: string = nombre, municipioFila?: string): NombreSinAforo {
  // `nombre` viene ya desconcatenado (M01); el recorte sobre él es el de EXT-07b, sin cambios.
  // CANONIC: la letra que falta en la cabeza («ENTRO DE TRANSFORMACION») se repone antes de la caja.
  const nombreComparable = aCajaRAE(reponerLetraPorCatalogo(nombre.replace(BLOQUE_AFORO, '').trim()));
  const nombreClean = conMunicipioAyuntamiento(nombreComparable, municipioFila);
  // El aforo y la comparación con el original se hacen sobre la celda cruda.
  const m = BLOQUE_AFORO.exec(nombreRaw);
  const nombreRawSinAforo = (m ? nombreRaw.slice(0, m.index) : nombreRaw).trim();
  const out: NombreSinAforo = { nombreClean, nombreComparable, nombreRawSinAforo };
  if (!m) return out;
  const aforo = extractAforo(m[0].replace(/_/g, ' ').trim(), 'nombre');
  if (aforo) {
    out.aforoDeclarado = aforo.value;
    out.aforoUnidad = aforo.unit;
    out.aforoFuente = 'nombre';
    out.aforoConfianza = aforo.confidence;
  }
  return out;
}

// Lazy-load de ExcelJS (reemplaza SheetJS/xlsx — SEC-02)
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- typeof import() necesario para lazy-load sin importar en top-level
type ExcelJSModule = typeof import('exceljs');
let EXCELJS_CACHE: ExcelJSModule | null = null;
async function getExcelJS(): Promise<ExcelJSModule> {
  if (!EXCELJS_CACHE) {
    EXCELJS_CACHE = await import('exceljs');
  }
  return EXCELJS_CACHE;
}

// TAB-FIX-S1 (26-jul-2026): lector de hoja de cálculo → SheetJS.
// Motivo, medido en AUDIT_F0_TABULAR_20260726: ExcelJS NO tiene lector de ODS
// (lib/ = csv·doc·stream·utils·xlsx) y, al ser el .ods un ZIP, workbook.xlsx.load
// NO lanza: devuelve 0 hojas y la extracción retornaba vacío en silencio.
// 6 de 6 .ods reales del corpus → 0 registros, 0 excepciones, con 54 registros presentes.
// Sobre SEC-02 (19-mar-2026, xlsx@0.18.5 → ExcelJS por GHSA-4r6h-8v6p-xvw6 y GHSA-5pgg-2g8v-p4x9):
// ambas están corregidas en 0.19.3 y 0.20.2 respectivamente. Aquí se usa el tarball
// vendorizado 0.20.3 (package.json → dependencies), ya en producción para ODSExporter.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- typeof import() necesario para lazy-load sin importar en top-level
type SheetJSModule = typeof import('xlsx');
let SHEETJS_CACHE: SheetJSModule | null = null;
async function getSheetJS(): Promise<SheetJSModule> {
  if (!SHEETJS_CACHE) {
    SHEETJS_CACHE = await import('xlsx');
  }
  return SHEETJS_CACHE;
}

// ============================================================================
// E2: INTEGRACIÓN DETECTOR ED50
// ============================================================================

/**
 * E2: Mapea resultado de detectarED50() al tipo ED50Detection del sistema.
 * 
 * @param resultado - Resultado del detector ED50
 * @returns Objeto ED50Detection para incluir en metadata
 */
function mapED50ResultToDetection(
  resultado: ReturnType<typeof detectarED50>
): ED50Detection {
  return {
    detected: resultado.posibleED50,
    confidence: resultado.confianza,
    warning: resultado.warning,
    recommendation: resultado.recomendacion,
    indicatorCount: resultado.indicadoresEncontrados.length,
  };
}

/**
 * E2: Extrae fecha de creación/modificación de metadatos ODT.
 * Los metadatos ODT están en meta.xml dentro del ZIP.
 * 
 * @param zip - Archivo ZIP del ODT
 * @returns Fecha más antigua encontrada (creación o modificación), o null
 */
async function extractODTDate(zip: JSZip): Promise<Date | null> {
  try {
    const metaXml = await zip.file('meta.xml')?.async('text');
    if (!metaXml) return null;
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(metaXml, 'text/xml');
    
    // Namespace de Dublin Core (fechas de documento)
    const dcNS = 'http://purl.org/dc/elements/1.1/';
    const metaNS = 'urn:oasis:names:tc:opendocument:xmlns:meta:1.0';
    
    // Intentar dc:date (fecha principal)
    let dateStr = doc.getElementsByTagNameNS(dcNS, 'date')[0]?.textContent;
    
    // Fallback: meta:creation-date
    if (!dateStr) {
      dateStr = doc.getElementsByTagNameNS(metaNS, 'creation-date')[0]?.textContent;
    }
    
    if (dateStr) {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    return null;
  } catch {
    // Error silencioso, fecha no es crítica
    return null;
  }
}

/**
 * E2: Extrae texto completo para análisis ED50.
 * Solo extrae los primeros ~50KB para optimizar rendimiento.
 * 
 * @param contentXml - Contenido XML del documento
 * @returns Texto extraído para análisis
 */
function extractTextForED50Analysis(contentXml: string): string {
  // Limitar a primeros 50KB para rendimiento
  const maxLength = 50000;
  const truncated = contentXml.length > maxLength 
    ? contentXml.substring(0, maxLength) 
    : contentXml;
  
  // Extraer solo texto, eliminar tags XML
  const textContent = truncated
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  return textContent;
}

// ============================================================================
// TIPOS Y CONSTANTES
// ============================================================================

/** Resultado de análisis de estructura de tabla */
interface TableStructure {
  hasNameColumn: boolean;
  hasAddressColumn: boolean;
  hasCoordColumns: boolean;
  hasCoordsInHeader: boolean;
  nameColIdx: number;
  addressColIdx: number;
  typeColIdx: number;
  xColIdx: number;
  yColIdx: number;
  freeTextColIdx: number;  // EXT-06: Índice de columna con texto libre (observaciones, notas)
  telefonoColIdx: number;  // IEP-03: Índice de columna de teléfono/contacto
  emailColIdx: number;     // IEP-03: Índice de columna de email/correo
  ownerColIdx: number;     // NOMTIT: columna de titular, solo si está en la misma fila de cabecera que «Nombre»
  dataStartRow: number;
  confidence: number; // 0-100
}

/** 
 * Patrones para detectar columnas - v3.1 AMPLIADOS
 * 
 * CAMBIOS v3.1:
 * - Eliminados anchors ^...$ para permitir texto adicional
 * - Añadidos patrones para "X - Longitud", "y- Latitud"
 * - Soporte para headers con saltos de línea "Coordenadas\n(UTM)"
 * - Soporte para columnas __EMPTY adyacentes a coordenadas
 */
const COLUMN_PATTERNS = {
  // Nombres de infraestructura - permite texto adicional
  name: /\b(nombre|denominaci[oó]n|descripci[oó]n|elemento|infraestructura|instalaci[oó]n)\b/i,
  
  // Direcciones - permite texto adicional  
  address: /\b(direcci[oó]n|ubicaci[oó]n|localizaci[oó]n|domicilio|emplazamiento)\b/i,
  
  // Tipo/categoría
  type: /\b(tipo|tipolog[ií]a|categor[ií]a|clase|naturaleza)\b/i,
  
  // Coordenada X - v3.4: Prioridad a "X" simple, añadido "Coordenada X"
  // Orden: X solo -> X con separadores -> palabras completas -> "coord...x"
  coordX: /^x$|^x\s*[-_]|\b(longitud|este|easting)\b|\bcoordenada\s*x\b|coord[^y]*x/i,
  
  // Coordenada Y - v3.4: Prioridad a "Y" simple, añadido "Coordenada Y"
  // Orden: Y solo -> Y con separadores -> palabras completas -> "coord...y"  
  coordY: /^y$|^y\s*[-_]|\b(latitud|norte|northing)\b|\bcoordenada\s*y\b|coord[^x]*y/i,
  
  // Coordenadas combinadas - "Coordenadas", "Coordenadas (UTM)", "Coordenadas\n(UTM- Geográficas)"
  coordCombined: /coordenadas?/i,
  
  // Propietario/titular. NOMTIT: sin «responsable» (casaba con las tablas de personal:
  // «Nombre de la entidad ¦ Responsable ¦ Cargo…») y sin \b final (reconoce la cabecera
  // pegada «Entidadtitular» de la ficha 250331 de Cortes y Graena).
  owner: /\b(titular|propietario|entidad|organismo)/i,
  
  // EXT-06: Campos de texto libre que pueden contener coordenadas embebidas
  freeText: /\b(observaci[oó]n(es)?|notas?|comentarios?|descripci[oó]n|detalles?|informaci[oó]n)\b/i,

  // IEP-03: Columnas de contacto
  telefono: /\btel[eé]fonos?\b|\btelef\.?\b|\bcontacto\b|\bm[oó]vil\b/i,
  email:    /\bemail\b|\bcorreo\b|\be-?mail\b/i,
};

/** Secciones PTEL conocidas que contienen infraestructuras */
const KNOWN_SECTIONS = [
  /patrimonio\s*(hist[oó]rico|cultural|natural)?/i,
  /elementos?\s*vulnerables?/i,
  /infraestructuras?\s*(cr[ií]ticas?|esenciales?)?/i,
  /servicios?\s*(esenciales?|b[aá]sicos?)/i,
  /centros?\s*(sanitarios?|educativos?|deportivos?)/i,
  /instalaciones?\s*(deportivas?|municipales?)/i,
  /recursos?\s*(municipales?|materiales?)/i,
  /medios?\s*(materiales?|humanos?)/i,
];

/**
 * FIX v3.9: Detecta si un header es una medición de longitud/distancia (NO coordenada).
 * 
 * Casos que debe detectar como medición (NO coordenada):
 * - "Longitud dentro del Municipio (m)"
 * - "Longitud (km)"
 * - "Longitud total"
 * - "Longitud en metros"
 * 
 * Casos que NO debe detectar (SÍ son coordenadas):
 * - "X - Longitud" (formato Hornos, es sub-header de coordenada)
 * - "Longitud" solo (término geodésico para coordenada X)
 * 
 * @param header - Texto del header a validar
 * @returns true si es medición de distancia, false si puede ser coordenada
 */
export function isLengthMeasurementHeader(header: string): boolean {
  if (!header || typeof header !== 'string') return false;
  
  const h = header.trim().toLowerCase();
  
  // Si es solo "longitud" sin contexto, es coordenada geodésica
  if (h === 'longitud') return false;
  
  // Si tiene prefijo "x" o "coord", es coordenada
  if (/^x\s*[-_]/.test(h) || /coord/i.test(h)) return false;
  
  // Patrones que indican medición de distancia (NO coordenada):
  // - Seguido de unidades: (m), (km), metros, kilómetros
  // - Seguido de contexto: "dentro", "total", "recorrido", "del cauce", "del río"
  const measurementPatterns = [
    /longitud\s*\(/i,                    // "Longitud (" seguido de unidades
    /longitud\s+\w*\s*(m|km|metro)/i,    // "Longitud ... m/km/metros"
    /longitud\s+(dentro|total|del|en)/i, // "Longitud dentro/total/del/en"
    /longitud.*municipio/i,              // "Longitud...municipio"
    /longitud.*cauce/i,                  // "Longitud...cauce"
    /longitud.*r[ií]o/i,                 // "Longitud...río"
    /longitud.*recorrido/i,              // "Longitud...recorrido"
  ];
  
  return measurementPatterns.some(pattern => pattern.test(h));
}

/**
 * FIX v3.9: Valida que una tabla tenga datos con coordenadas UTM reales.
 * 
 * Verifica que al menos una fila de datos contenga valores en rango UTM
 * en las columnas detectadas como X e Y.
 * 
 * @param rows - Filas de la tabla
 * @param xColIdx - Índice de columna X detectada
 * @param yColIdx - Índice de columna Y detectada
 * @param dataStartRow - Fila donde empiezan los datos
 * @returns true si la tabla contiene coordenadas UTM válidas
 */
export function tableHasValidUTMData(
  rows: string[][],
  xColIdx: number,
  yColIdx: number,
  dataStartRow: number
): boolean {
  if (xColIdx < 0 && yColIdx < 0) return false;
  
  // Verificar primeras 5 filas de datos
  const rowsToCheck = Math.min(5, rows.length - dataStartRow);
  let validCount = 0;
  
  for (let i = 0; i < rowsToCheck; i++) {
    const row = rows[dataStartRow + i];
    if (!row) continue;
    
    const xVal = xColIdx >= 0 && xColIdx < row.length ? cleanCoordinateValue(row[xColIdx]) : '';
    const yVal = yColIdx >= 0 && yColIdx < row.length ? cleanCoordinateValue(row[yColIdx]) : '';
    
    const hasValidX = xVal && isValidUTMValue(xVal, 'x');
    const hasValidY = yVal && isValidUTMValue(yVal, 'y');
    
    // Contar si tiene al menos una coordenada válida (X o Y)
    if (hasValidX || hasValidY) {
      validCount++;
    }
  }
  
  // Requiere al menos 1 fila con coordenadas válidas
  return validCount >= 1;
}

/** Patrones para clasificar tipo de dirección */
const ADDRESS_PATTERNS = {
  postal: /^(c\/|calle|avda?\.?|avenida|plaza|pza\.?|paseo|ctra\.?|carretera|camino|travesía|ronda|glorieta)\s/i,
  toponym: /^(paraje|cerro|cortijo|barranco|arroyo|fuente|era|cañada|llano|loma|monte|sierra|vega|haza)\s/i,
  cadastral: /^(pol[ií]gono|parcela|pol\.?\s*\d|parc\.?\s*\d|\d+[-\/]\d+)/i,
  sports: /(piscina|campo\s*(de\s*)?(f[uú]tbol|golf)|polideportivo|pabell[oó]n|gimnasio|pista)/i,
  telecom: /(antena|repetidor|torre\s*(de\s*)?comunicaci[oó]n|bts|estaci[oó]n\s*base)/i,
  road: /^(carretera|autov[ií]a|autopista|ctra\.?|[anc][-\s]?\d{1,4}|gr[-\s]?\d{3,4})/i,
};

// ============================================================================
// IEP-08: EXTRACTOR DE REFERENCIAS CATASTRALES EN TEXTO LIBRE
// ============================================================================

/**
 * Resultado de extracción de referencia catastral de una celda de texto libre.
 * Al menos uno de los tres campos estará presente si hay alguna coincidencia.
 */
interface CadastralExtraction {
  /** Referencia catastral completa de 20 dígitos (Catastro DGC) */
  refCatastral?: string;
  /** Número de polígono (para CatastroR2SearchOptions.polygon) */
  poligono?: string;
  /** Número de parcela (para CatastroR2SearchOptions.parcel) */
  parcela?: string;
}

/**
 * Ref. catastral DGC — dos formatos:
 *   - 20 chars: 5d + 1L(hoja) + 12d(parcela+subparcela) + 2L(control)  ej: 04069A015000250000EK
 *   - 14 chars: 5d + 1L(hoja) + 8d(parcela)                            ej: 04069A01500025
 */
const REFCAT_FULL_RE = /\b(\d{5}[A-Z]\d{12}[A-Z]{2}|\d{5}[A-Z]\d{8})\b/;

/**
 * Patrón polígono/parcela en texto libre:
 * - "Polígono 15, Parcela 234"
 * - "Pol. 15 / Parc. 234"
 * - "pol 8 parc 120"
 * - "15/234" (solo dígitos separados por /, sin contexto de calle)
 *
 * OBS-PARSER-CADASTRAL-POL-PARC-DASH (06-jun-2026): admitir además el formato
 * abreviado con guion y marcador «P» sin literal «parcela», frecuente en los PTEL
 * (corpus Colomera): «Pol 14- P146», «Pol 10- P690», «Pol. 8 P-398»,
 * «Pol 7 - parc 29,30,36». El separador polígono→marcador acepta guion/espacio/
 * coma/barra; el marcador acepta «parcela»/«parc»/«p»; en parcelas múltiples
 * («P 36,30,29») se captura la primera.
 */
const POL_PARC_RE =
  /(?:pol[ií]gono|pol\.?)\s*[:\-]?\s*(\d{1,4})[\s,\/\-]+(?:parcela|parc|p)\.?\s*[:\-]?\s*(\d{1,5})/i;

/**
 * IEP-08: Extrae referencia catastral de cualquier texto libre de la fila.
 * Prioridad: refcat completa > polígono+parcela.
 * Devuelve null si no encuentra nada relevante.
 */
export function extractCadastralRef(text: string): CadastralExtraction | null {
  if (!text || text.length < 6) return null;

  // 1. Referencia catastral completa (20 o 14 chars)
  const fullMatch = REFCAT_FULL_RE.exec(text);
  if (fullMatch) {
    return { refCatastral: fullMatch[1] };
  }

  // 2. Polígono + parcela
  const polMatch = POL_PARC_RE.exec(text);
  if (polMatch) {
    return { poligono: polMatch[1], parcela: polMatch[2] };
  }

  return null;
}

/**
 * IEP-08: Escanea múltiples celdas de una fila en busca de referencia catastral.
 * Devuelve la primera coincidencia encontrada, o null si ninguna la tiene.
 */
function extractCadastralRefFromRow(cells: string[]): CadastralExtraction | null {
  for (const cell of cells) {
    const result = extractCadastralRef(cell ?? '');
    if (result) return result;
  }
  return null;
}

// ============================================================================
// UTILIDADES
// ============================================================================

function generateId(): string {
  return `inf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Formato detectado para el documento actual (cache)
let currentDocFormat: DocumentFormat = 'plain';

/**
 * Establece el formato del documento actual para optimizar el pipeline.
 * Llamar al inicio del procesamiento de cada documento.
 */
export function setDocumentFormat(format: DocumentFormat): void {
  currentDocFormat = format;
}

/**
 * Limpia y normaliza un valor de coordenada con pipeline de filtros.
 * FMT-03: Aplica primero el pipeline según formato, luego limpieza específica.
 */
export function cleanCoordinateValue(val: string): string {
  if (!val || typeof val !== 'string') return '';
  
  // FMT-03: Pre-procesar con pipeline según formato detectado
  const preProcessed = pipelinePreProcess(val, currentDocFormat);
  let cleaned = preProcessed.cleaned.trim();
  
  // Detectar placeholders
  if (/^(indicar|sin\s*datos?|-+|n\/?[ac]|\.+|_+|xxx?|completar)$/i.test(cleaned)) {
    return '';
  }
  
  // Eliminar comillas especiales
  cleaned = cleaned.replace(/[´`''""]+/g, '');
  
  // Espacios como separadores de miles: "437 686" → "437686", "4 076 367" → "4076367"
  // CUIDADO: "437 686 30" podría tener decimales → "437686.30"
  if (/^\d[\d\s]+\d$/.test(cleaned) && cleaned.includes(' ')) {
    const groups = cleaned.split(/\s+/);
    
    // Si todos los grupos (excepto el primero) tienen exactamente 3 dígitos,
    // son separadores de miles puro (ej: "4 076 367", "524 538")
    const allThreeDigitGroups = groups.slice(1).every(g => g.length === 3);
    
    if (allThreeDigitGroups) {
      // Solo unir sin añadir punto decimal
      cleaned = groups.join('');
    } else {
      // El último grupo tiene distinta longitud, podría ser decimal
      const noSpaces = cleaned.replace(/\s/g, '');
      const lastGroup = groups[groups.length - 1];
      if (lastGroup.length === 2 && noSpaces.length > 6) {
        // Último grupo de 2 dígitos = probable decimal
        cleaned = noSpaces.slice(0, -2) + '.' + noSpaces.slice(-2);
      } else {
        cleaned = noSpaces;
      }
    }
  }
  
  // Puntos como separadores de miles (ej: "524.891" → "524891", "4.230.105" → "4230105")
  const dotCount = (cleaned.match(/\./g) || []).length;
  if (dotCount > 1) {
    // Múltiples puntos = claramente separadores de miles (ej: 4.230.105)
    cleaned = cleaned.replace(/\./g, '');
  } else if (dotCount === 1) {
    const parts = cleaned.split('.');
    // Si la parte después del punto tiene exactamente 3 dígitos, es separador de miles
    // Ejemplos: "524.891" → "524891", "4230.105" → "4230105"
    // PERO NO: "437301.8" (decimal legítimo con 1 dígito)
    if (parts[1]?.length === 3 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
      // Verificar que el resultado sería una coordenada válida
      const combined = parseInt(parts[0] + parts[1]);
      // Para X: debe ser 100000-800000 (6 dígitos que empiezan con 1-8)
      // Para Y: debe ser 3980000-4320000 (7 dígitos que empiezan con 39-43)
      const isValidX = combined >= 100000 && combined <= 800000;
      const isValidY = combined >= 3980000 && combined <= 4320000;
      if (isValidX || isValidY) {
        cleaned = cleaned.replace('.', '');
      }
    }
  }
  
  // Coma por punto decimal
  cleaned = cleaned.replace(',', '.');
  
  // Limpiar espacios
  cleaned = cleaned.replace(/\s/g, '');
  
  return cleaned;
}

/**
 * Verifica si un valor es una coordenada UTM válida para Andalucía
 */
export function isValidUTMValue(val: string, type: 'x' | 'y'): boolean {
  const num = parseFloat(val);
  if (isNaN(num) || num === 0) return false;
  
  // G1/γ (AUDIT-DUP 08-jul): los MÍNIMOS beben de ANDALUCIA_BOUNDS_UTM30 (fuente única).
  // Corrige el Y-min que perdía el sur (Tarifa, northing ~3,99 M < 4 M): 25 coords reales
  // rescatadas, 0 regresión medida en los 25 fixtures. Los MÁXIMOS quedan deliberadamente
  // generosos: detección ≠ contención (un gate de detección estricto perdería coords de
  // borde; la contención estricta la hace GATE-P1-S1). Ver §6 análisis conjunto parsers.
  if (type === 'x') {
    return num >= ANDALUCIA_BOUNDS_UTM30.minX && num <= 800000;
  } else {
    return num >= ANDALUCIA_BOUNDS_UTM30.minY && num <= 4350000;
  }
}

/**
 * FORK-A (A.1, β · convergencia acotada, AUDIT-DUP 08-jul): motor ÚNICO de coordenada
 * para el fallback de extracción. Reusa `parseCoordinateInput` (grados decimales WGS84,
 * DMS, UTM huso 29/30, PlusCode, formato europeo) + `toUtmInZone` (reproyección a
 * EPSG:25830) — exactamente el mismo motor vivo de la edición manual de P2
 * (CoordinateFields). Se invoca SOLO como último recurso, cuando el gate UTM estrecho
 * `isValidUTMValue` no encontró coordenada, por lo que NO altera el ~80 % de celdas
 * UTM30 ya sanas (riesgo de regresión nulo sobre lo sano; sin round-trip de precisión).
 * Cierra la divergencia extracción↔edición: rescata Zalabí (grados) y huso 29 (Huelva).
 *
 * @param text - Cadena con el par de coordenada (orden lat/lon indiferente: el parser
 *               autodetecta y corrige mediante `needsLatLonSwap`).
 * @returns X/Y en UTM30 (EPSG:25830) o null si no se reconoce o cae fuera de Andalucía.
 */
export function parseCoordinateToUtm30(
  text: string,
): { x: number; y: number; format: string } | null {
  if (!text || !text.trim()) return null;
  const parsed = parseCoordinateInput(text);
  if (!parsed) return null;
  try {
    const utm = toUtmInZone(parsed.lat, parsed.lon, 'EPSG:25830');
    // Cordura: no colar ruido reproyectado fuera de Andalucía (misma constante que G1).
    if (
      utm.x < ANDALUCIA_BOUNDS_UTM30.minX || utm.x > ANDALUCIA_BOUNDS_UTM30.maxX ||
      utm.y < ANDALUCIA_BOUNDS_UTM30.minY || utm.y > ANDALUCIA_BOUNDS_UTM30.maxY
    ) {
      return null;
    }
    return { x: utm.x, y: utm.y, format: parsed.format };
  } catch {
    return null;
  }
}

/**
 * EXT-05: Detecta y separa coordenadas concatenadas en una misma celda.
 * Ejemplos: "524538 4229920", "524538;4229920", "524538/4229920"
 * 
 * IMPORTANTE: Distingue separadores de miles de coordenadas concatenadas:
 * - "4 076 367" = separadores de miles (grupos de 3 dígitos) → NO separar
 * - "524538 4229920" = coordenadas concatenadas → SÍ separar
 * 
 * @param value - Valor de celda que puede contener coordenadas concatenadas
 * @returns Objeto con X e Y separadas, o null si no se detectó concatenación
 */
export function splitConcatenatedCoordinates(
  value: string
): { x: string; y: string } | null {
  if (!value || typeof value !== 'string') return null;
  
  const trimmed = value.trim();
  
  // Separadores soportados: espacio, punto y coma, slash, tabulador
  // El espacio se procesa al final por ser más complejo (puede ser separador de miles)
  const separators = [';', '/', '\t'];
  
  // Intentar separadores simples primero (no ambiguos)
  for (const sep of separators) {
    if (trimmed.includes(sep)) {
      const parts = trimmed.split(sep).map(p => p.trim()).filter(p => p);
      if (parts.length === 2) {
        const cleanX = cleanCoordinateValue(parts[0]);
        const cleanY = cleanCoordinateValue(parts[1]);
        
        // Verificar que son coordenadas UTM válidas
        if (isValidUTMValue(cleanX, 'x') && isValidUTMValue(cleanY, 'y')) {
          return { x: cleanX, y: cleanY };
        }
        // Probar orden inverso (Y X)
        if (isValidUTMValue(cleanX, 'y') && isValidUTMValue(cleanY, 'x')) {
          return { x: cleanY, y: cleanX };
        }
      }
    }
  }
  
  // Separador espacio: más complejo porque puede ser separador de miles
  if (trimmed.includes(' ')) {
    const groups = trimmed.split(/\s+/).filter(g => g);
    
    // Si todos los grupos excepto el primero tienen 3 dígitos, es separador de miles
    // Ej: "4 076 367" (3 grupos: 1, 3, 3 dígitos)
    if (groups.length >= 2) {
      const allThousandsSeparators = groups.slice(1).every(g => /^\d{3}$/.test(g)) &&
                                     /^\d{1,3}$/.test(groups[0]);
      
      if (allThousandsSeparators) {
        // Es separador de miles, no coordenadas concatenadas
        return null;
      }
    }
    
    // Caso típico: 2 grupos de números "524538 4229920"
    if (groups.length === 2) {
      const cleanX = cleanCoordinateValue(groups[0]);
      const cleanY = cleanCoordinateValue(groups[1]);
      
      if (isValidUTMValue(cleanX, 'x') && isValidUTMValue(cleanY, 'y')) {
        return { x: cleanX, y: cleanY };
      }
      // Probar orden inverso (Y X)
      if (isValidUTMValue(cleanX, 'y') && isValidUTMValue(cleanY, 'x')) {
        return { x: cleanY, y: cleanX };
      }
    }
    
    // Caso con separadores de miles en una de las coordenadas:
    // "524 538 4229920" → "524538" + "4229920"
    if (groups.length >= 3) {
      // Intentar primeras N partes como X, resto como Y
      for (let split = 1; split < groups.length; split++) {
        const xParts = groups.slice(0, split).join('');
        const yParts = groups.slice(split).join('');
        
        const cleanX = cleanCoordinateValue(xParts);
        const cleanY = cleanCoordinateValue(yParts);
        
        if (isValidUTMValue(cleanX, 'x') && isValidUTMValue(cleanY, 'y')) {
          return { x: cleanX, y: cleanY };
        }
        if (isValidUTMValue(cleanX, 'y') && isValidUTMValue(cleanY, 'x')) {
          return { x: cleanY, y: cleanX };
        }
      }
    }
  }
  
  return null;
}

/**
 * EXT-01: Busca una coordenada en columnas adyacentes.
 * Útil cuando X existe pero Y falta (o viceversa) y Y está en columna ±1.
 * 
 * @param cells - Array de celdas de la fila
 * @param refColIdx - Índice de la columna de referencia (donde está la otra coordenada)
 * @param type - Tipo de coordenada a buscar ('x' o 'y')
 * @returns Objeto con valor limpio y índice de columna, o null si no encontrado
 */
export function findAdjacentCoordinate(
  cells: string[],
  refColIdx: number,
  type: 'x' | 'y'
): { value: string; colIdx: number } | null {
  if (refColIdx < 0 || refColIdx >= cells.length) return null;
  
  // Primero buscar en columna +1 (más común: X en col N, Y en col N+1)
  const nextIdx = refColIdx + 1;
  if (nextIdx < cells.length) {
    const cleaned = cleanCoordinateValue(cells[nextIdx]);
    if (cleaned && isValidUTMValue(cleaned, type)) {
      return { value: cleaned, colIdx: nextIdx };
    }
  }
  
  // Luego buscar en columna -1
  const prevIdx = refColIdx - 1;
  if (prevIdx >= 0) {
    const cleaned = cleanCoordinateValue(cells[prevIdx]);
    if (cleaned && isValidUTMValue(cleaned, type)) {
      return { value: cleaned, colIdx: prevIdx };
    }
  }
  
  return null;
}

/**
 * EXT-03: Detecta cuántas filas son headers/sub-headers en una tabla.
 * Analiza las primeras filas para identificar:
 * - Filas de título de sección (pocas celdas no vacías, texto largo fusionado)
 * - Filas de headers principales (contienen términos como Nombre, Dirección, Coordenadas)
 * - Filas de sub-headers de coordenadas (solo X, Y, Longitud, Latitud, etc.)
 * 
 * @param rows - Array de filas de la tabla
 * @param maxRowsToCheck - Máximo de filas a analizar (default: 5)
 * @returns Número de filas que son headers (dataStartRow será este valor)
 */
export function detectMultiRowHeaderCount(
  rows: string[][],
  maxRowsToCheck: number = 5
): number {
  if (rows.length === 0) return 0;
  
  let headerCount = 0;
  
  // Patrones para detectar filas de header
  const headerPatterns = [
    // Headers principales
    /\b(nombre|denominaci[oó]n|descripci[oó]n|elemento|infraestructura)\b/i,
    /\b(direcci[oó]n|ubicaci[oó]n|localizaci[oó]n|domicilio)\b/i,
    /\b(tipo|tipolog[ií]a|categor[ií]a|clase)\b/i,
    /coordenadas?/i,
  ];
  
  // Patrones para sub-headers de coordenadas
  const coordSubHeaderPattern = /^[xy\s\-_]+(longitud|latitud|utm|coord)?$/i;
  const coordTermsPattern = /\b(longitud|latitud|este|norte|easting|northing|utm)\b/i;
  
  // Patrones para títulos de sección (textos largos en celdas fusionadas)
  const sectionTitlePatterns = [
    /elementos?\s*(vulnerables?|afectados?)/i,
    /infraestructuras?\s*(cr[ií]ticas?|esenciales?|municipales?)/i,
    /recursos?\s*(materiales?|humanos?|municipales?)/i,
    /medios?\s*(materiales?|humanos?)/i,
    /centros?\s*(sanitarios?|educativos?)/i,
    /patrimonio/i,
    /inventario/i,
  ];
  
  for (let rowIdx = 0; rowIdx < Math.min(maxRowsToCheck, rows.length); rowIdx++) {
    const row = rows[rowIdx];
    // TAB-FIX-S1 (DOP-TAB-DISPERSA): array disperso → `c` puede ser undefined y
    // `.trim()` estallaba. Mismo motivo que el blindaje de analyzeTableStructure.
    const nonEmptyCells = row.filter(c => String(c ?? '').trim());
    const rowText = nonEmptyCells.join(' ').toLowerCase();
    
    // Caso 1: Fila de título de sección (1-2 celdas no vacías, contiene patrón de sección)
    if (nonEmptyCells.length <= 2) {
      const isSectionTitle = sectionTitlePatterns.some(p => p.test(rowText));
      if (isSectionTitle) {
        headerCount = rowIdx + 1;
        continue;
      }
    }
    
    // Caso 2: Fila de headers principales (contiene términos de header)
    const hasHeaderTerms = headerPatterns.some(p => p.test(rowText));
    if (hasHeaderTerms && nonEmptyCells.length >= 2) {
      headerCount = rowIdx + 1;
      continue;
    }
    
    // Caso 3: Fila de sub-headers de coordenadas (solo X, Y, Longitud, etc.)
    // Debe tener ≤4 celdas no vacías y contener términos de coordenadas
    if (nonEmptyCells.length <= 4) {
      // Verificar si es sub-header de coordenadas
      const isCoordSubHeader = 
        coordSubHeaderPattern.test(rowText) ||
        (coordTermsPattern.test(rowText) && /\bx\b.*\by\b|\by\b.*\bx\b/i.test(rowText)) ||
        (nonEmptyCells.length <= 2 && row.some(c => /^[xy]$/i.test(c.trim()))) ||
        // EXT-03: Detectar filas con solo "Longitud" y "Latitud" (sin X/Y explícitos)
        (nonEmptyCells.length <= 2 && /\blongitud\b/i.test(rowText) && /\blatitud\b/i.test(rowText));
      
      if (isCoordSubHeader) {
        headerCount = rowIdx + 1;
        continue;
      }
    }
    
    // Si llegamos aquí y ya detectamos headers, esta fila probablemente es datos
    // Pero verificamos que no sea una fila mayormente vacía (celda fusionada continuada)
    if (headerCount > 0) {
      const totalCells = row.length;
      const emptyCells = row.filter(c => !String(c ?? '').trim() || String(c ?? '').startsWith('__EMPTY')).length;
      const emptyRatio = emptyCells / totalCells;
      
      // Si >80% de celdas están vacías y estamos en filas tempranas, puede ser header fusionado
      if (emptyRatio > 0.8 && rowIdx < 3) {
        headerCount = rowIdx + 1;
        continue;
      }
      
      // Esta fila parece ser datos, dejamos de buscar headers
      break;
    }
    
    // Si no hemos encontrado ningún header aún y esta fila tiene datos significativos
    // (más de 2 celdas no vacías con contenido variado), asumimos que es la primera fila de datos
    if (nonEmptyCells.length > 2) {
      // Verificar que no sea un header que no reconocimos
      const looksLikeData = nonEmptyCells.some(c => {
        const num = parseFloat(cleanCoordinateValue(c));
        return !isNaN(num) && num > 1000; // Parece coordenada o número grande
      });
      
      if (looksLikeData) {
        break; // Encontramos datos, dejamos de buscar headers
      }
    }
  }
  
  // Asegurar que al menos devolvemos 1 si encontramos patrones de header
  return Math.max(headerCount, 0);
}

/**
 * EXT-02: Busca columnas con header vacío o "__EMPTY*" adyacentes a una columna de coordenadas.
 * Útil para XLSX con celdas fusionadas donde "Coordenadas" es header común y X/Y están debajo.
 * 
 * @param headerRow - Array de celdas del header
 * @param coordsColIdx - Índice de la columna donde se encontró "Coordenadas"
 * @returns Objeto con índices de X e Y, o null si no encontrados
 */
export function findEmptyColumnsForCoords(
  headerRow: string[],
  coordsColIdx: number
): { xColIdx: number; yColIdx: number } | null {
  if (coordsColIdx < 0 || coordsColIdx >= headerRow.length) return null;
  
  // Patrón para detectar columnas vacías o __EMPTY generadas por XLSX
  const isEmptyHeader = (h: string): boolean => {
    const header = h.trim().toLowerCase();
    return header === '' || header.startsWith('__empty');
  };
  
  // Buscar columnas adyacentes (i+1, i+2) que estén vacías o sean __EMPTY
  const nextIdx1 = coordsColIdx + 1;
  const nextIdx2 = coordsColIdx + 2;
  
  // Caso 1: La columna "Coordenadas" tiene X debajo y siguiente tiene Y
  // Headers: [... | Coordenadas | __EMPTY | ...] 
  // Datos:   [... | X           | Y       | ...]
  if (nextIdx1 < headerRow.length && isEmptyHeader(headerRow[nextIdx1])) {
    // Tenemos al menos una columna vacía adyacente
    // X estará en coordsColIdx, Y en nextIdx1
    if (nextIdx2 < headerRow.length && isEmptyHeader(headerRow[nextIdx2])) {
      // Dos columnas vacías: X en nextIdx1, Y en nextIdx2
      return { xColIdx: nextIdx1, yColIdx: nextIdx2 };
    }
    // Solo una columna vacía: X en coordsColIdx, Y en nextIdx1
    return { xColIdx: coordsColIdx, yColIdx: nextIdx1 };
  }
  
  // Caso 2: "Coordenadas" está fusionada y debajo hay X/Y en la misma columna y siguiente
  // Si la propia columna "Coordenadas" contiene datos de X debajo
  return null;
}

/**
 * Detecta municipio del nombre del archivo.
 * 
 * v4.0 (Sprint 1 - 09-Ene-2026):
 * - FIX CRÍTICO: Ya no devuelve "Ayuntamiento" ni "Ayto"
 * - Soporte para nombres multi-palabra: "La Guardia de Jaén", "El Cuervo de Sevilla"
 * - Expansión de abreviaturas: "d" → "de", "Jaen" → "Jaén"
 * - Lista negra de términos genéricos que NO son municipios
 * 
 * Patrones soportados:
 * - YYMMDD_PTEL_MUNICIPIO (250124_PTEL_JUBRIQUE)
 * - YYMMDD_Ficha_PTEL_Ayto_MUNICIPIO (250702_...Ayto Colomera)
 * - YYMMDD_Ficha_PTEL_Ayuntamiento_MUNICIPIO (250304_...Ayuntamiento_La Guardia d Jaen)
 * - PTEL_MUNICIPIO_AÑO (PTEL Mancha Real 2025)
 * - PTEL MUNICIPIO (PTEL El Cuervo de Sevilla)
 */
export function detectMunicipality(fileName: string): string | undefined {
  // Lista negra: términos que NUNCA son municipios válidos
  const blacklist = new Set([
    'ayuntamiento', 'ayto', 'municipio', 'ptel', 'ficha', 'plantilla',
    'coordenadas', 'infraestructuras', 'elementos', 'vulnerables'
  ]);
  
  // Limpiar extensión y normalizar separadores.
  // NFDNOM: macOS entrega los nombres de fichero en NFD (tilde combinante
  // U+0301) y el navegador los pasa tal cual en `File.name`; los patrones de
  // abajo solo admiten vocales acentuadas precompuestas, así que sin pasar a
  // NFC «La Guardia de Jaén» no casaba con ninguno.
  const cleanName = fileName
    .normalize('NFC')
    .replace(/\.(odt|xlsx?|csv|pdf)$/i, '')
    .replace(/_/g, ' ')
    .trim();
  
  // Patrones ordenados por especificidad (más específico primero)
  const patterns = [
    // Patrón 1: "...Ayuntamiento MUNICIPIO" o "...Ayto MUNICIPIO"
    // Captura todo después de Ayuntamiento/Ayto hasta el final o hasta año
    /(?:Ayuntamiento|Ayto\.?)\s+([A-Za-záéíóúñÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s'\-]+?)(?:\s+\d{4})?$/i,
    
    // Patrón 2: "PTEL MUNICIPIO" (sin prefijo de fecha)
    /^PTEL\s+([A-Za-záéíóúñÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s'\-]+?)(?:\s+\d{4})?$/i,
    
    // Patrón 3: "YYMMDD PTEL MUNICIPIO" 
    /^\d{6}\s*[_\s]*PTEL\s+([A-Za-záéíóúñÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s'\-]+?)$/i,
    
    // Patrón 4: "...PTEL MUNICIPIO" genérico (captura multi-palabra)
    /PTEL\s+([A-Za-záéíóúñÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s'\-]+?)(?:\s+\d{4})?$/i,
    
    // Patrón 5: "MUNICIPIO PTEL" (municipio antes de PTEL)
    /^([A-Za-záéíóúñÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s'\-]+?)\s+PTEL/i,
  ];
  
  for (const pattern of patterns) {
    const match = cleanName.match(pattern);
    if (match?.[1]) {
      let name = match[1].trim();
      
      // Verificar que no sea término de blacklist
      if (blacklist.has(name.toLowerCase())) continue;
      
      // Expandir abreviaturas comunes
      name = name
        .replace(/\bd\b/gi, 'de')           // "d" → "de"
        .replace(/\bdel?\s+$/i, '')         // Eliminar "de" o "del" al final
        .replace(/\s+/g, ' ')               // Normalizar espacios
        .trim();
      
      // Normalizar acentos en nombres conocidos
      const acentos: Record<string, string> = {
        'jaen': 'Jaén',
        'malaga': 'Málaga',
        'almeria': 'Almería',
        'cordoba': 'Córdoba',
        'cadiz': 'Cádiz',
        'guenjar': 'Quéntar', // Variante
        'quentar': 'Quéntar',
        'zalabi': 'Zalabí',
      };
      
      // Aplicar capitalización correcta
      const particles = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'y', 'e']);
      name = name
        .toLowerCase()
        .split(/\s+/)
        .map((word, idx) => {
          // Verificar si hay acento conocido
          if (acentos[word]) return acentos[word];
          // Primera palabra siempre capitalizada
          if (idx === 0) return word.charAt(0).toUpperCase() + word.slice(1);
          // Partículas en minúscula
          if (particles.has(word)) return word;
          // Resto capitalizadas
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
      
      // Verificar longitud mínima
      if (name.length >= 3) {
        return name;
      }
    }
  }
  
  return undefined;
}

/**
 * Detecta provincia basándose en el municipio, contra el catálogo canónico
 * INE de los 785 municipios andaluces (`buscarMunicipio`, con normalización
 * de acentos y fuzzy de homónimos).
 *
 * PRV2 (OBS-P1-PROVINCIA-FALLBACK-GRANADA, reapertura): sustituye al
 * diccionario parcial hardcoded (~50 municipios) con doble fallback mudo a
 * «Granada», que inventaba la provincia para los ~735 municipios restantes
 * (caso real: Benizalón 04027 mostrado como «Granada» en cabecera y exports).
 * Sin match en el catálogo → '' — nunca inventar (HX-FALLBACK-ENMASCARA-BUG).
 */
export function detectProvince(municipality?: string): string {
  if (!municipality) return '';
  const res = buscarMunicipio(municipality);
  if (res.encontrado && res.provincia) return res.provincia;
  return '';
}

/**
 * IEP-03: Extrae una dirección alternativa del texto libre de observaciones.
 * 
 * Busca patrones que indican una ubicación física ("junto al ayuntamiento",
 * "ctra. de Granada km 3", "C/ Mayor 15", etc.) y los devuelve como candidato
 * de dirección para el orquestador de geocodificación.
 * 
 * Descarta textos que son claramente horarios, teléfonos, URLs o códigos.
 */
export function extractDireccionAlt(observaciones: string): string | undefined {
  if (!observaciones || observaciones.length < 5) return undefined;

  // Descartar si parece teléfono, URL, coordenada numérica pura o código
  if (/^[\d\s\-+().]{6,20}$/.test(observaciones.trim())) return undefined;
  if (/https?:\/\//i.test(observaciones)) return undefined;
  if (/^\d{5,9}[.,]\d{3,9}$/.test(observaciones.trim())) return undefined;

  // Patrones positivos: presencia de indicadores de localización física
  const patronesDireccion = [
    // Tipos de vía: los que tienen word boundary limpio
    /\b(calle|avda?\.?|avenida|plaza|pza\.?|paseo|ctra\.?|carretera|camino|polígono|pol\.)\b/i,
    // c/ y cl. requieren tratamiento especial (la / y . rompen \b final)
    /(?:^|\s)c[/\\]/i,
    /(?:^|\s)cl\./i,
    /\bkm\.?\s*\d/i,
    /\bjunto\s+al?\b/i,            // "junto a" y "junto al"
    /\bfrente\s+al?\b/i,
    /\ba\s+\d+\s*m\b/i,          // "a 200 m del"
    /\bdetr[aá]s\s+de\b/i,
    /\bacceso\s+(por|desde)\b/i,
    /\bsalida\s+\d/i,             // "salida 3 de la A-92"
    /\bbarrio\b|\bbarriada\b|\bpedanía\b/i,
    /\bparaje\b|\bparaje\s+\w/i,
    /\bpol[ií]gono\s+industrial\b/i,
    /n[uú]m\.?\s*\d+/i,           // "núm. 15"
    /n[oº°]\s*\d+/i,              // "nº 5"
  ];

  const tienePatronDireccion = patronesDireccion.some(p => p.test(observaciones));
  if (!tienePatronDireccion) return undefined;

  // Limpiar y truncar: eliminar coordenadas embebidas, normalizar espacios
  const limpio = observaciones
    .replace(/\b\d{6,7}(?:[.,]\d{5,7}|\s+\d{7,8})\b/g, '') // eliminar coords UTM (con sep . , o espacio)
    .replace(/\s{2,}/g, ' ')
    .trim()
    .substring(0, 200);

  return limpio.length >= 5 ? limpio : undefined;
}

/**
 * Clasifica el tipo de dirección para determinar estrategia de geocodificación
 */
export function classifyAddressType(address: string, name: string): AddressType {
  const combined = `${name} ${address}`.toLowerCase();
  
  // Orden importa: más específico primero
  if (ADDRESS_PATTERNS.telecom.test(combined)) return 'telecom';
  if (ADDRESS_PATTERNS.sports.test(combined)) return 'sports';
  if (ADDRESS_PATTERNS.road.test(name)) return 'road';
  if (ADDRESS_PATTERNS.cadastral.test(address)) return 'cadastral';
  // IEP-08: refcat completa en la dirección también es tipo cadastral
  if (REFCAT_FULL_RE.test(address) || POL_PARC_RE.test(address)) return 'cadastral';
  if (ADDRESS_PATTERNS.toponym.test(address)) return 'toponym';
  if (ADDRESS_PATTERNS.postal.test(address)) return 'postal';
  
  // Si tiene número de portal, probablemente es postal
  if (/,?\s*\d+\s*$/.test(address) || /n[ºo°]?\s*\d+/i.test(address)) return 'postal';
  
  // Si tiene nombre de calle común
  if (/s\/n|sin\s*n[úu]mero/i.test(address)) return 'postal';
  
  return 'unknown';
}

/**
 * Detecta el tipo de infraestructura desde nombre y contexto
 */
export function detectInfrastructureType(name: string, type: string, tableName: string): string {
  const combined = `${name} ${type} ${tableName}`.toLowerCase();
  
  if (/patrimonio|hist[oó]rico|cultural|monumento|iglesia|ermita|museo|castillo|torre|puente/i.test(combined)) return 'PATRIMONIO';
  if (/sanita|salud|m[eé]dico|consultorio|hospital|ambulatorio|farmacia|centro\s*de\s*salud/i.test(combined)) return 'SANITARIO';
  if (/educa|enseñanza|colegio|instituto|escuela|ceip|ies|guarder[ií]a|infantil/i.test(combined)
      || tieneSiglaEducativaPunteada(combined)) return 'EDUCATIVO';
  if (/seguridad|polic[ií]a|guardia\s*civil|bomberos|protecci[oó]n\s*civil/i.test(combined)) return 'SEGURIDAD';
  if (/telecom|antena|repetidor|comunicaci[oó]n|telef[oó]nica|movistar|vodafone|orange/i.test(combined)) return 'TELECOMUNICACIONES';
  if (/deport|piscina|polideportivo|campo|pabell[oó]n|gimnasio|pista/i.test(combined)) return 'DEPORTIVO';
  if (/hidr[aá]ulic|agua|dep[oó]sito|embalse|edar|depuradora|potabilizadora/i.test(combined)) return 'HIDRAULICO';
  if (/energ[ií]a|el[eé]ctric|subestaci[oó]n|transformador|endesa|iberdrola/i.test(combined)) return 'ENERGIA';
  if (/municipal|ayuntamiento|administrativo|casa\s*consist/i.test(combined)) return 'MUNICIPAL';
  if (/vial|carretera|camino|acceso|autopista|autov[ií]a/i.test(combined)) return 'VIAL';
  if (/recreativ|parque|[aá]rea\s*recreativa|jard[ií]n/i.test(combined)) return 'RECREATIVO';
  
  return 'OTRO';
}

// ============================================================================
// EXT-04: DETECCIÓN DE TABLAS ANIDADAS EN ODT
// ============================================================================

/** Información de una tabla anidada encontrada */
interface NestedTableInfo {
  table: Element;
  parentTableName: string;
  sectionContext: string;
  depth: number;
}

/**
 * EXT-04: Detecta tablas anidadas dentro de celdas de una tabla.
 * Busca elementos table:table dentro de los elementos table:table-cell.
 * 
 * @param table - Elemento tabla padre
 * @param tableNS - Namespace de tablas ODT
 * @param parentName - Nombre de la tabla padre
 * @param depth - Nivel de anidamiento actual
 * @returns Array de información de tablas anidadas encontradas
 */
export function findNestedTables(
  table: Element,
  tableNS: string,
  parentName: string,
  depth: number = 0
): NestedTableInfo[] {
  const nestedTables: NestedTableInfo[] = [];
  
  // Obtener todas las celdas de la tabla padre
  const cells = table.getElementsByTagNameNS(tableNS, 'table-cell');
  
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    
    // Buscar tablas directamente dentro de esta celda (no recursivo profundo)
    const childTables = cell.getElementsByTagNameNS(tableNS, 'table');
    
    for (let j = 0; j < childTables.length; j++) {
      const nestedTable = childTables[j];
      
      // Verificar que es hijo directo o cercano (evitar tablas muy anidadas)
      // Subir hasta encontrar un table-cell o hasta 3 niveles
      let parent = nestedTable.parentElement;
      let levels = 0;
      let isDirectChild = false;
      
      while (parent && levels < 5) {
        if (parent === cell) {
          isDirectChild = true;
          break;
        }
        // Si encontramos otra tabla antes de llegar a la celda, no es hijo directo
        if (parent.localName === 'table' && parent !== table) {
          break;
        }
        parent = parent.parentElement;
        levels++;
      }
      
      if (isDirectChild) {
        // Extraer contexto de la celda (texto antes de la tabla)
        const sectionContext = extractSectionContext(cell, tableNS);
        
        nestedTables.push({
          table: nestedTable,
          parentTableName: parentName,
          sectionContext,
          depth: depth + 1
        });
      }
    }
  }
  
  return nestedTables;
}

/**
 * EXT-04: Extrae el contexto de sección de una celda que contiene una tabla anidada.
 * Busca texto de sección PTEL antes de la tabla (ej: "Patrimonio Histórico").
 * 
 * IMPORTANTE: Solo procesa hijos directos de la celda para evitar capturar
 * texto de dentro de subtablas.
 * 
 * @param cell - Celda que contiene la tabla anidada
 * @param tableNS - Namespace de tablas ODT
 * @returns Texto de contexto de sección encontrado, o string vacío
 */
export function extractSectionContext(cell: Element, tableNS: string): string {
  const textNS = 'urn:oasis:names:tc:opendocument:xmlns:text:1.0';
  let contextText = '';
  
  // Iterar solo sobre hijos directos de la celda
  // Esto evita procesar párrafos dentro de subtablas
  for (let i = 0; i < cell.childNodes.length; i++) {
    const child = cell.childNodes[i] as Element;
    
    // Si encontramos una tabla, detenernos - ya no hay contexto antes de la tabla
    if (child.localName === 'table' && child.namespaceURI === tableNS) {
      break;
    }
    
    // Solo procesar elementos de párrafo directos
    if (child.localName === 'p' && child.namespaceURI === textNS) {
      const text = child.textContent?.trim() || '';
      
      // Detectar si es un título de sección PTEL
      if (text.length > 3 && text.length < 100) {
        const isSectionTitle = KNOWN_SECTIONS.some(pattern => pattern.test(text));
        // Priorizar títulos de sección conocidos sobre mayúsculas genéricas
        if (isSectionTitle) {
          contextText = text;
        } else if (!contextText && /^[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ\s]+$/u.test(text)) {
          // Solo usar mayúsculas si no hay mejor contexto ya
          contextText = text;
        }
      }
    }
  }
  
  return contextText;
}

/**
 * EXT-04: Verifica si una tabla es una tabla anidada (tiene padre que es otra tabla).
 * 
 * @param table - Elemento tabla a verificar
 * @param tableNS - Namespace de tablas ODT
 * @returns true si es tabla anidada, false si es tabla de nivel superior
 */
export function isNestedTable(table: Element, tableNS: string): boolean {
  let parent = table.parentElement;
  let levels = 0;
  
  while (parent && levels < 10) {
    // Si encontramos una celda de tabla, esta tabla está anidada
    if (parent.localName === 'table-cell' && 
        parent.namespaceURI === tableNS) {
      return true;
    }
    // Si encontramos el cuerpo del documento, no está anidada
    if (parent.localName === 'body' || parent.localName === 'document-content') {
      return false;
    }
    parent = parent.parentElement;
    levels++;
  }
  
  return false;
}

// ============================================================================
// EXTRACCIÓN DE CELDAS ODT - BUG-PARSE-001: Soporte multirow
// ============================================================================

/** Párrafos extraídos de una celda ODT */
export interface CellParagraphs {
  paragraphs: string[];
  repeatCount: number;
}

/** Configuración de expansión multirow */
const MULTIROW_CONFIG = {
  minParagraphsMultiCell: 2,   // Mínimo párrafos para considerar celda "multi"
  minNonEmptyCells: 2,         // Mínimo celdas con contenido para expandir
  maxExpandedRows: 200,        // Límite de seguridad
};

/**
 * BUG-PARSE-001: Extrae párrafos individuales de una celda ODT.
 * Cada <text:p> es un párrafo separado que puede representar una infraestructura distinta.
 * 
 * @param cell - Elemento table-cell del DOM
 * @param textNS - Namespace de texto ODT
 * @returns Array de strings (un string por párrafo no vacío)
 */
export function extractParagraphsFromCell(cell: Element, textNS: string): string[] {
  const paragraphs: string[] = [];
  const pElements = cell.getElementsByTagNameNS(textNS, 'p');
  
  for (let i = 0; i < pElements.length; i++) {
    const text = pElements[i].textContent?.trim() || '';
    // Incluir párrafos no vacíos
    if (text) {
      // AFOROP2 (DOP-1 a): «AFORO…» en párrafo aparte pertenece al párrafo anterior de la misma
      // casilla («CPR ZALABI. SEDE ALCUDIA» / «AFORO: 48»). Sin unirlo, una fila sin northing UTM
      // se expande por párrafos y el aforo cae en una fila fantasma que descarta FIX B-02.
      // Guarda: no se une a un párrafo de plantilla vacía («_____»), que debe seguir descartándose.
      // Medido sobre el corpus (29-sep-2026): 18 aforos recuperados, 2 135 filas sin cambio.
      if (paragraphs.length > 0 && PARRAFO_AFORO.test(text)
          && /[\p{L}\d]/u.test(paragraphs[paragraphs.length - 1])) {
        paragraphs[paragraphs.length - 1] += ' ' + text;
      } else {
        paragraphs.push(text);
      }
    }
  }
  
  return paragraphs;
}

/**
 * BUG-PARSE-001: Procesa fila extrayendo párrafos por celda.
 * Esta versión preserva la estructura de párrafos para permitir expansión posterior.
 * 
 * @param row - Elemento table-row del DOM
 * @param tableNS - Namespace de tablas ODT
 * @param textNS - Namespace de texto ODT
 * @returns Array de CellParagraphs (párrafos por celda)
 */
export function getRowCellsWithParagraphs(
  row: Element,
  tableNS: string,
  textNS: string
): CellParagraphs[] {
  const cells = row.getElementsByTagNameNS(tableNS, 'table-cell');
  const result: CellParagraphs[] = [];
  
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    const repeatAttr = cell.getAttribute('table:number-columns-repeated');
    const repeatCount = repeatAttr ? parseInt(repeatAttr, 10) : 1;
    const paragraphs = extractParagraphsFromCell(cell, textNS);
    
    // Limitar repeticiones para evitar arrays enormes
    const effectiveRepeat = Math.min(repeatCount, paragraphs.length > 0 ? 15 : 2);
    for (let r = 0; r < effectiveRepeat; r++) {
      result.push({ paragraphs: [...paragraphs], repeatCount: 1 });
    }
  }
  
  return result;
}

/**
 * BUG-PARSE-001: Analiza si una fila necesita expansión multirow.
 * Detecta filas donde múltiples celdas tienen múltiples párrafos alineados.
 * 
 * Caso Colomera: Una fila con 6 celdas, cada una con ~30 párrafos.
 * 
 * @param cellsWithParagraphs - Párrafos por celda de la fila
 * @returns true si la fila debe expandirse a múltiples filas
 */
export function shouldExpandRow(cellsWithParagraphs: CellParagraphs[]): boolean {
  const paragraphCounts = cellsWithParagraphs.map(c => c.paragraphs.length);
  const nonEmptyCells = paragraphCounts.filter(count => count > 0).length;
  
  // Requiere mínimo 2 celdas con contenido
  if (nonEmptyCells < MULTIROW_CONFIG.minNonEmptyCells) {
    return false;
  }

  // MAPCOLS-ENERGIA (19-jul-2026): guard anti-fila-envuelta.
  // Distingue una fila APILADA legítima (Colomera: N infraestructuras apiladas,
  // coordenadas también apiladas o ausentes) de una fila ENVUELTA por wrapping
  // visual de un ÚNICO registro (Guadix Tabla77: nombre/tipo partidos en varios
  // <text:p>, pero X/Y en UNA sola celda de 1 párrafo). Si existe ≥1 celda que es
  // una coordenada UTM-Y (northing) de un solo párrafo, la fila es envuelta:
  // expandirla alinearía párrafos por índice y descartaría la X/Y en las filas
  // fantasma (j>0). Se delega a la rama legacy (join de párrafos), que preserva
  // la coordenada. El northing (≥3,99 M) no colisiona con aforo/año/superficie.
  const hasSingleParagraphNorthing = cellsWithParagraphs.some(
    c => c.paragraphs.length === 1 &&
         isValidUTMValue(cleanCoordinateValue(c.paragraphs[0]), 'y')
  );
  if (hasSingleParagraphNorthing) {
    return false;
  }

  // Contar celdas con múltiples párrafos
  const multiCellCount = paragraphCounts.filter(
    count => count >= MULTIROW_CONFIG.minParagraphsMultiCell
  ).length;
  
  // Expandir si ≥2 celdas tienen ≥2 párrafos cada una
  return multiCellCount >= 2;
}

/**
 * BUG-PARSE-001: Expande fila multirow a múltiples filas.
 * Alinea párrafos por índice: párrafo[0] de cada celda forma fila 1, etc.
 * 
 * Ejemplo Colomera:
 * - Celda0 (Actividad): ["Ferretería", "Taller", ...30 total]
 * - Celda1 (Nombre): ["Atalaya", "Antonio Ortega", ...30 total]
 * Resultado: 30 filas, cada una con (Ferretería, Atalaya), (Taller, Antonio Ortega), etc.
 * 
 * @param cellsWithParagraphs - Párrafos por celda de la fila original
 * @returns Array de filas expandidas (cada fila es string[])
 */
export function expandMultiParagraphRow(cellsWithParagraphs: CellParagraphs[]): string[][] {
  const maxParagraphs = Math.max(...cellsWithParagraphs.map(c => c.paragraphs.length), 0);
  
  if (maxParagraphs <= 1) {
    // No hay expansión necesaria, devolver fila simple
    return [cellsWithParagraphs.map(c => c.paragraphs[0] || '')];
  }
  
  const rowCount = Math.min(maxParagraphs, MULTIROW_CONFIG.maxExpandedRows);
  const expandedRows: string[][] = [];
  
  for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
    const row: string[] = [];
    
    for (const cell of cellsWithParagraphs) {
      if (rowIdx < cell.paragraphs.length) {
        row.push(cell.paragraphs[rowIdx]);
      } else {
        // Celda con menos párrafos: dejar vacío
        row.push('');
      }
    }
    
    // Solo incluir filas con al menos una celda no vacía
    if (row.some(cell => cell.trim() !== '')) {
      expandedRows.push(row);
    }
  }
  
  return expandedRows;
}

/**
 * Extrae celdas de una fila ODT (modo legacy - concatenado).
 * Se mantiene para compatibilidad con filas que NO necesitan expansión.
 */
function _getRowCells(row: Element, ns: string): string[] {
  const cells = row.getElementsByTagNameNS(ns, 'table-cell');
  const result: string[] = [];
  
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    const repeatAttr = cell.getAttribute('table:number-columns-repeated');
    const repeatCount = repeatAttr ? parseInt(repeatAttr, 10) : 1;
    const text = cell.textContent?.trim() || '';
    
    // Limitar repeticiones para evitar arrays enormes
    const effectiveRepeat = Math.min(repeatCount, text ? 15 : 2);
    for (let r = 0; r < effectiveRepeat; r++) {
      result.push(text);
    }
  }
  
  return result;
}

// ============================================================================
// MJ-08 / MJ-10: FUSIÓN DE FRAGMENTOS DE NOMBRE EN COLUMNAS ADYACENTES
// ============================================================================

/**
 * MJ-08 + MJ-10: Detecta y fusiona celdas horizontales contiguas que forman
 * un nombre fragmentado en tablas ODT con columnas estrechas.
 *
 * MJ-08 (base): celda termina en preposición / paréntesis abierto / coma.
 * MJ-10 (ext):  cadenas de >2 celdas (bucle), guión de truncación, espacio
 *               final en celda raw (señal ODT de continuación).
 *
 * IMPORTANTE: Solo opera sobre la columna de NOMBRE (nameColIdx).
 * Las columnas de coordenadas, dirección y tipo no se modifican.
 *
 * @param row        - Fila de celdas ya extraídas (post-getRowCells)
 * @param nameColIdx - Índice de la columna nombre (de analyzeTableStructure)
 * @returns Fila con celdas de nombre fusionadas si procede; sin cambios si no
 */
export function mergeAdjacentCellFragments(
  row: string[],
  nameColIdx: number,
): string[] {
  if (nameColIdx < 0 || nameColIdx >= row.length - 1) return row;

  // Señales de fragmento (MJ-08): preposición / conjunción / paréntesis / coma
  const FRAGMENT_ENDINGS =
    /(\bde|\bdel|\bla|\blos|\blas|\bel|\ben|\by|\bo|\ba|\bpor|\bcon|[,(])$/i;

  // MJ-10a: guión de truncación tipográfica al final de celda
  const TRAILING_HYPHEN = /-\s*$/;

  const merged = [...row];
  const MAX_MERGES = 4; // límite de seguridad para cadenas largas
  let mergeCount = 0;

  while (mergeCount < MAX_MERGES) {
    const rawCell = merged[nameColIdx]; // sin trim → permite detectar espacio final
    const cell = rawCell.trim();

    if (!cell) break;
    if (cell.length >= 35) break; // celda larga = nombre completo, no fusionar

    // MJ-10: localizar siguiente celda no vacía (salta huecos de fusiones previas)
    let nextIdx = nameColIdx + 1;
    while (nextIdx < merged.length && merged[nextIdx].trim() === '') {
      nextIdx++;
    }
    if (nextIdx >= merged.length) break;

    const nextCell = merged[nextIdx].trim();
    if (!nextCell) break;

    // No fusionar si la siguiente celda parece coordenada / número puro
    if (/^[\d\s.,]+$/.test(nextCell)) break;

    // MJ-10b: espacio final en celda raw = señal ODT de continuación de texto
    const hasTrailingSpace = rawCell !== rawCell.trimEnd();

    const isFragment =
      FRAGMENT_ENDINGS.test(cell) ||
      TRAILING_HYPHEN.test(cell) ||
      hasTrailingSpace;

    if (!isFragment) break;

    // Fusionar: sin espacio si era guión de palabra partida, con espacio en el resto
    const separator = TRAILING_HYPHEN.test(cell) ? '' : ' ';
    const cellBase = cell.replace(/-\s*$/, '');
    const fusionado = `${cellBase}${separator}${nextCell}`.trim();

    console.log(
      `[MJ-08/10] Fusión columnas: "${cell}" + "${nextCell}" → "${fusionado}"`,
    );

    merged[nameColIdx] = fusionado;
    merged[nextIdx] = ''; // vaciar celda absorbida
    mergeCount++;
    // No avanzar nameColIdx: re-evaluar el resultado en la siguiente iteración
    // para detectar cadenas de >2 celdas (MJ-10)
  }

  return merged;
}

// ============================================================================
// ANÁLISIS DE ESTRUCTURA DE TABLA
// ============================================================================

/**
 * Analiza la estructura de una tabla para determinar si contiene infraestructuras
 */
function analyzeTableStructure(rows: string[][]): TableStructure {
  const result: TableStructure = {
    hasNameColumn: false,
    hasAddressColumn: false,
    hasCoordColumns: false,
    hasCoordsInHeader: false,
    nameColIdx: -1,
    addressColIdx: -1,
    typeColIdx: -1,
    xColIdx: -1,
    yColIdx: -1,
    freeTextColIdx: -1, // EXT-06: Columna de texto libre (observaciones, notas)
    telefonoColIdx: -1, // IEP-03: Columna de teléfono/contacto
    emailColIdx:    -1, // IEP-03: Columna de email/correo
    ownerColIdx:    -1, // NOMTIT: Columna de titular
    dataStartRow: 1,
    confidence: 0,
  };
  
  if (rows.length < 2) return result;

  // NOMTIT: fila de cabecera donde aparecen «Nombre» y el titular; solo cuentan si coinciden
  let nameHeaderRow = -1;
  let ownerHeaderRow = -1;
  
  // EXT-03: Detectar cuántas filas son headers (puede ser 2, 3 o más)
  const multiRowHeaderCount = detectMultiRowHeaderCount(rows);
  
  // Analizar primeras N filas como posibles headers (mínimo 3, o lo detectado)
  const rowsToAnalyze = Math.max(3, multiRowHeaderCount + 1);
  for (let headerRow = 0; headerRow < Math.min(rowsToAnalyze, rows.length); headerRow++) {
    const cells = rows[headerRow];
    
    for (let i = 0; i < cells.length; i++) {
      // TAB-FIX-S1 (DOP-TAB-DISPERSA): defensa ante array disperso. Un hueco
      // (`<1 empty item>`) da `undefined` y `.toLowerCase()` estallaba. La ruta
      // tabular ya entrega filas densas, pero esta funcion la comparte la ruta ODT.
      const cell = String(cells[i] ?? '').toLowerCase().trim();
      if (!cell) continue;
      
      // Detectar columnas por patrón
      if (result.nameColIdx === -1 && COLUMN_PATTERNS.name.test(cell)) {
        result.nameColIdx = i;
        result.hasNameColumn = true;
        nameHeaderRow = headerRow;
      }
      if (result.ownerColIdx === -1 && i !== result.nameColIdx
          && COLUMN_PATTERNS.owner.test(cell) && !COLUMN_PATTERNS.name.test(cell)) {
        result.ownerColIdx = i;
        ownerHeaderRow = headerRow;
      }
      if (result.addressColIdx === -1 && COLUMN_PATTERNS.address.test(cell)) {
        result.addressColIdx = i;
        result.hasAddressColumn = true;
      }
      if (result.typeColIdx === -1 && COLUMN_PATTERNS.type.test(cell)) {
        result.typeColIdx = i;
      }
      if (result.xColIdx === -1 && COLUMN_PATTERNS.coordX.test(cell)) {
        // FIX v3.9: Verificar que NO sea medición de longitud (metros/km)
        if (!isLengthMeasurementHeader(cells[i])) {
          result.xColIdx = i;
          result.hasCoordColumns = true;
        }
      }
      if (result.yColIdx === -1 && COLUMN_PATTERNS.coordY.test(cell)) {
        result.yColIdx = i;
        result.hasCoordColumns = true;
      }
      // Columna combinada "Coordenadas"
      if (COLUMN_PATTERNS.coordCombined.test(cell)) {
        result.hasCoordsInHeader = true;
        // EXT-02: Guardar posición para buscar __EMPTY después
        // Por ahora, asignar provisionalmente (se ajustará luego si hay __EMPTY)
        if (result.xColIdx === -1) result.xColIdx = i;
        if (result.yColIdx === -1) result.yColIdx = i + 1;
        result.hasCoordColumns = true;
      }
      // EXT-06: Detectar columna de texto libre (observaciones, notas, descripción)
      if (result.freeTextColIdx === -1 && COLUMN_PATTERNS.freeText.test(cell)) {
        result.freeTextColIdx = i;
      }
      // IEP-03: Detectar columnas de teléfono y email
      if (result.telefonoColIdx === -1 && COLUMN_PATTERNS.telefono.test(cell)) {
        result.telefonoColIdx = i;
      }
      if (result.emailColIdx === -1 && COLUMN_PATTERNS.email.test(cell)) {
        result.emailColIdx = i;
      }
    }
    
    // EXT-02: Si hay "Coordenadas" en header pero X/Y no se detectaron bien,
    // buscar columnas __EMPTY adyacentes (caso típico: celda fusionada en XLSX)
    if (result.hasCoordsInHeader && result.xColIdx >= 0) {
      const emptyResult = findEmptyColumnsForCoords(cells, result.xColIdx);
      if (emptyResult) {
        // Encontramos columnas vacías/__EMPTY, actualizar índices
        result.xColIdx = emptyResult.xColIdx;
        result.yColIdx = emptyResult.yColIdx;
      }
    }
    
    // Si encontramos estructura, los datos empiezan después
    if (result.hasNameColumn || result.hasAddressColumn || result.hasCoordColumns) {
      // EXT-03: Usar el máximo entre la fila actual+1 y el conteo multi-fila detectado
      result.dataStartRow = Math.max(headerRow + 1, multiRowHeaderCount);
      
      // Verificar si siguiente fila es sub-header de coordenadas (X/Y, Longitud/Latitud)
      // v3.2: Mejorado para detectar sub-headers con texto adicional
      if (result.dataStartRow < rows.length) {
        const nextRow = rows[result.dataStartRow];
        const nonEmptyCells = nextRow.filter(c => c.trim());
        const nextText = nonEmptyCells.join(' ').toLowerCase().trim();
        
        // Detectar sub-header si:
        // 1. Solo contiene X, Y, espacios, guiones
        // 2. O tiene ≤4 celdas no vacías y contiene X e Y (incluyendo "X - Longitud", "y- Latitud")
        // 3. O contiene palabras clave de coordenadas como "longitud", "latitud", "utm"
        const isCoordSubHeader = 
          /^[xy\s\-_]+$/i.test(nextText) || 
          (nonEmptyCells.length <= 4 && /\bx\b.*\by\b/i.test(nextText)) ||
          (nonEmptyCells.length <= 4 && /(longitud|latitud|utm)/i.test(nextText));
        
        if (isCoordSubHeader) {
          // Es sub-header, buscar índices X/Y aquí usando patrones más flexibles
          for (let i = 0; i < nextRow.length; i++) {
            const c = nextRow[i].toLowerCase().trim();
            if (!c) continue;
            // Detectar X: "x", "x-", "x - longitud", "longitud", etc.
            if (result.xColIdx === -1 && (c === 'x' || /^x\s*[-_]/.test(c) || /\blongitud\b/.test(c) || /\beste\b/.test(c))) {
              result.xColIdx = i;
            }
            // Detectar Y: "y", "y-", "y - latitud", "latitud", etc.
            if (result.yColIdx === -1 && (c === 'y' || /^y\s*[-_]/.test(c) || /\blatitud\b/.test(c) || /\bnorte\b/.test(c))) {
              result.yColIdx = i;
            }
          }
          result.dataStartRow++;
        }
      }
      break;
    }
  }
  
  // NOMTIT: el titular solo vale si comparte fila de cabecera con «Nombre»
  // (evita leer «Titular de la Alcaldía» de una fila de datos de las tablas de personal).
  if (ownerHeaderRow !== nameHeaderRow) {
    result.ownerColIdx = -1;
  }

  // Si no encontramos nombre por header, usar primera columna
  if (result.nameColIdx === -1 && rows[0]?.length > 0) {
    result.nameColIdx = 0;
  }
  
  // Calcular confianza
  let confidence = 0;
  if (result.hasNameColumn) confidence += 30;
  if (result.hasAddressColumn) confidence += 30;
  if (result.hasCoordColumns) confidence += 40;
  result.confidence = confidence;
  
  return result;
}

// ============================================================================
// NOMTIT: «Nombre» y «Entidad titular» cambiados de columna por el autor
// ============================================================================

/**
 * Forma de entidad (titular) en una celda. Tolerante a texto pegado del ODT
 * («CONSEJERIADEEDUCACION») y a erratas del corpus («AYUMTAMIENTO»).
 */
const RE_FORMA_ENTIDAD = new RegExp(
  '^(excmo\\.?\\s*)?(ayu[nm]t?amiento|ayto\\.?|junta\\s*(de\\s*)?andaluc|s\\.?\\s*a\\.?\\s*s\\.?\\s|' +
  'diputaci|consejer|mancomunidad|particular|privad[oa]|p[uú]blic[oa]$|municipal$|municipio de|' +
  'estado$|ministerio|confederaci|endesa|j\\.?\\s*a\\.?$|obispado|arzobispado)',
  'i',
);

/** Umbrales medidos en el corpus (46 tablas con titular: sanas ≤ 15 %, cambiadas ≥ 67 %). */
const NOMTIT_MIN_ENTIDAD_EN_NOMBRE = 0.6;
const NOMTIT_MAX_ENTIDAD_EN_TITULAR = 0.3;

/** G1: celda de subcabecera de coordenadas (X, Y, Longitud, Latitud, «X Longitud»…). */
const RE_SUBCABECERA_COORD = /^([xy]|longitud|latitud|[xy][\s\-_]*(longitud|latitud))$/i;

function normalizaCeldaTitular(v: string | undefined): string {
  return (v ?? '').normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * NOMTIT: si en una tabla con columna de titular la columna «Nombre» trae casi
 * siempre una entidad (Ayuntamiento, Junta de Andalucía, Privado…) y la del
 * titular casi nunca, el autor cambió las dos columnas: se intercambian en las
 * filas de datos. Una fila cuyo titular está vacío (< 3 letras) conserva lo que
 * dice el documento, para no perderla. Devuelve una copia; no toca `rows`.
 */
export function intercambiarNombreTitular(
  rows: string[][],
  structure: {
    nameColIdx: number; ownerColIdx: number; dataStartRow: number;
    addressColIdx?: number; typeColIdx?: number; xColIdx?: number; yColIdx?: number;
  },
): { rows: string[][]; intercambiadas: number } {
  const n = structure.nameColIdx;
  const t = structure.ownerColIdx;
  if (n < 0 || t < 0 || n === t) return { rows, intercambiadas: 0 };

  // G2: estructura colapsada (cabecera en una sola celda: «Nombre» comparte columna con
  // dirección, tipo o coordenadas) → no se intercambia. Desde `TABGEM` las contenedoras
  // (origen medido de este caso) ya no llegan aquí; la guarda se conserva por prudencia.
  const otras = [structure.addressColIdx, structure.typeColIdx, structure.xColIdx, structure.yColIdx];
  if (otras.some(i => i !== undefined && i >= 0 && i === n)) return { rows, intercambiadas: 0 };

  // G1: las subcabeceras de coordenadas («X ¦ Y», «Longitud ¦ Latitud») no son datos.
  const esFilaDatos = (r: string[]) =>
    ((r[n] ?? '').trim() || (r[t] ?? '').trim()) &&
    !(RE_SUBCABECERA_COORD.test((r[n] ?? '').trim()) || RE_SUBCABECERA_COORD.test((r[t] ?? '').trim()));

  const datos = rows.slice(structure.dataStartRow).filter(esFilaDatos);
  if (datos.length < 2) return { rows, intercambiadas: 0 };

  const nombres = datos.map(r => normalizaCeldaTitular(r[n])).filter(Boolean);
  const titulares = datos.map(r => normalizaCeldaTitular(r[t])).filter(Boolean);
  const pNombre = nombres.filter(v => RE_FORMA_ENTIDAD.test(v)).length / Math.max(1, nombres.length);
  const pTitular = titulares.filter(v => RE_FORMA_ENTIDAD.test(v)).length / Math.max(1, titulares.length);
  if (pNombre < NOMTIT_MIN_ENTIDAD_EN_NOMBRE || pTitular > NOMTIT_MAX_ENTIDAD_EN_TITULAR) {
    return { rows, intercambiadas: 0 };
  }

  let intercambiadas = 0;
  const out = rows.map((r, i) => {
    if (i < structure.dataStartRow || !esFilaDatos(r)) return r;
    if ((r[t] ?? '').trim().length < 3) return r;
    const copia = [...r];
    copia[n] = r[t];
    copia[t] = r[n] ?? '';
    intercambiadas++;
    return copia;
  });
  console.log(`[NOMTIT] Nombre y titular cambiados en el documento: ${intercambiadas} filas intercambiadas`);
  return { rows: out, intercambiadas };
}

/**
 * F307 (INFADM-S2): detecta si un nombre es un placeholder de plantilla
 * «pendiente de rellenar» EMBEBIDO en texto de relleno más largo, que los
 * filtros anclados `^…$` no cazan (p. ej. «_____Indicar____________________»).
 *
 * Heurística D5-A (contenido dominante → casi vacío): tras retirar rellenos
 * (guiones, guiones bajos, puntos) y las palabras clave de plantilla, si el
 * residuo queda casi vacío Y había señal de plantilla (keyword o ≥3 «_»), es un
 * placeholder. La guarda del residuo evita falsos positivos: un nombre real que
 * contenga «indicar» («Colegio Indicar Norte») conserva residuo > 2 → no marca.
 */
export function esNombrePlaceholder(nombre: string): boolean {
  const s = (nombre ?? '').trim();
  if (!s) return false;
  const KW = '(indicar|completar|rellenar|pendiente|ejemplo|muestra)';
  const teniaSenal =
    new RegExp(`\\b${KW}\\b`, 'i').test(s) || /_{3,}/.test(s) || /^[\s\-._·•]+$/.test(s);
  if (!teniaSenal) return false;
  // Residuo tras quitar rellenos + keywords
  const residuo = s
    .replace(/[_\-.·•]+/g, ' ')
    .replace(new RegExp(`\\b${KW}\\b`, 'gi'), ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return residuo.length <= 2;
}

/**
 * ¿La fila aporta alguna señal estructural más allá del nombre? — tipo,
 * dirección o coordenada X/Y no vacías.
 *
 * Guarda anti-falso-descarte compartida por las dos reglas de
 * `isValidInfrastructureRow` que juzgan al nombre POR SU FORMA y no por su
 * contenido: la de fragmentos preposicionales (INFADM-S2) y la anti-persona
 * (T77V). Ambas aciertan sobre una lista de contactos y fallan sobre una tabla
 * de infraestructuras; la señal estructural es lo que distingue una de otra.
 */
function tieneSenalEstructural(cells: string[], structure: TableStructure): boolean {
  const val = (idx: number) => (idx >= 0 && idx < cells.length ? (cells[idx] ?? '').trim() : '');
  return (
    val(structure.typeColIdx).length > 0 ||
    val(structure.addressColIdx).length > 0 ||
    cleanCoordinateValue(val(structure.xColIdx)) !== '' ||
    cleanCoordinateValue(val(structure.yColIdx)) !== ''
  );
}

/**
 * Verifica si una fila contiene datos de infraestructura válidos
 */
function isValidInfrastructureRow(cells: string[], structure: TableStructure): boolean {
  // Debe tener un nombre válido
  const nameIdx = structure.nameColIdx >= 0 ? structure.nameColIdx : 0;
  const name = cells[nameIdx]?.trim() || '';
  
  // Filtrar nombres vacíos o muy cortos
  if (name.length < 3) return false;
  
  // Filtrar headers y sub-headers de coordenadas
  if (COLUMN_PATTERNS.name.test(name) || COLUMN_PATTERNS.address.test(name)) return false;
  if (/^[xy][-_\s]*(longitud|latitud|utm|coord)?$/i.test(name)) return false;
  if (/^(longitud|latitud|este|norte|easting|northing)$/i.test(name)) return false;
  
  // Filtrar placeholders
  if (/^(indicar|completar|rellenar|ejemplo|muestra|-+|\.+|_+|n\/a|sin\s*datos?)$/i.test(name)) return false;
  
  // Filtrar textos que son claramente descripciones de riesgos
  if (/^(movimiento|incendio|accidente|inundaci|sequ[ií]a|terremoto|epidemia|tipolog[ií]a)s?\s/i.test(name)) return false;
  
  // Filtrar nombres de personas (Nombre Apellido Apellido).
  // T77V: la forma «tres palabras capitalizadas» NO distingue a una persona de
  // un topónimo o un establecimiento escritos en Title Case, que es como los
  // redactan los PTEL. Medido sobre los 25 ODT del corpus: 76 filas retiradas,
  // 75 de ellas infraestructuras reales — «Parque De Bomberos», «Cuartel
  // Guardia Civil», «Policía Local Andújar», «Farmacia Serrano Arnau»,
  // «Estación De Ferrocarril», «Cortijo Del Cura»… Se aplica la MISMA guarda
  // anti-falso-descarte que la regla de fragmentos preposicionales de abajo:
  // solo se trata como persona si la fila no aporta ninguna otra señal
  // estructural (tipo / dirección / X / Y), que es el caso de la lista de
  // contactos para el que la regla se escribió. Una fila con coordenadas en una
  // tabla de infraestructuras se ingiere aunque su nombre parezca una persona:
  // errar ancho es abstenerse, errar estrecho es perder el elemento correcto.
  if (/^[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+$/.test(name)
      && !tieneSenalEstructural(cells, structure)) {
    return false;
  }
  
  // Filtrar metadatos de aforo — FIX B-02: cubre AFORO:4, AFORO:_____, AFORO: 40, AFORO: N PERSONAS
  if (/^AFORO[\s:_]*/i.test(name)) return false;

  // Filtrar tipologías sueltas usadas como nombre (fragmento de celda de tipo)
  if (/^(sanitario|cultural|educativo|social|industrial|energ[eé]tico|hidr[aá]ulico|seguridad|emergencias?|transporte|administrativo)s?$/i.test(name)) return false;

  // Filtrar fragmentos preposicionales o legales (inicio de frase incompleta).
  // INFADM-S2 (OBS-P1-PARSER-FILAS-NUCLEO-PERDIDAS): NO incluir los artículos
  // el/la/los/las — frontan topónimos legítimos con altísima frecuencia
  // (El Ejido, La Línea, Los Barrios, «La Fuente de la Higuera», «Los Medinas»…),
  // y su inclusión perdía filas reales en silencio. Las preposiciones/conjunciones
  // restantes solo indican fragmento si la fila carece de otra señal estructural
  // (tipo / dirección / coordenadas) → guarda anti-falso-descarte.
  if (/^(de|del|en|y|a|por|para|con|sobre|desde|hasta)\s/i.test(name)) {
    if (!tieneSenalEstructural(cells, structure)) return false;
  }
  if (/^(resoluci[oó]n|acuerdo|decreto|orden\s|ley\s|art[ií]culo|norma|disposici[oó]n)\s/i.test(name)) return false;
  
  // Filtrar textos genéricos sin valor
  if (/^(no existe|suplente|pendiente|sin\s*(constancia|determinar)|otros?)$/i.test(name)) return false;
  
  // Debe tener algo más que solo el nombre
  const nonEmptyCells = cells.filter(c => c.trim() && c.trim().length > 1);
  if (nonEmptyCells.length < 2) return false;
  
  return true;
}

// ============================================================================
// EXTRACTOR PRINCIPAL ODT
// ============================================================================

/**
 * FORK-A A.2 (α · convergencia total, AUDIT-DUP 08-jul): DETECTOR ÚNICO de coordenada de
 * fila para las dos rutas de extracción (ODT y hoja de cálculo). Un solo punto de decisión
 * que combina, en este orden:
 *   1. gate UTM nativo `isValidUTMValue` (X/Y ya en EPSG:25830) — SIN round-trip, preserva
 *      bit-exacto el ~80 % de coords sanas (|Δ| de reproyectar UTM nativo medido en 6,4 cm).
 *   2. EXT-01: rescate de la coordenada faltante en columna adyacente.
 *   3. rama motor `parseCoordinateToUtm30` (grados/DMS/huso-29/PlusCode → EPSG:25830), el
 *      mismo motor vivo en la edición P2, GATED por `allowDegreesMotor` para NO reproyectar
 *      lo nativo. En ODT = `hasLonLatSubcab`; en hoja = `false` (la detección de sub-cabecera
 *      lon/lat en hoja es costura de A.3, no se activa aquí).
 * Cierra la deriva `isValidUTMValue` (gate estrecho, extracción) ↔ `parseCoordinateInput`
 * (multiformato, edición): misma entrada → misma coordenada, en ambas rutas.
 */
function detectRowCoordinate(
  cells: string[],
  structure: TableStructure,
  cleanX: string,
  cleanY: string,
  allowDegreesMotor: boolean,
): { cleanX: string; cleanY: string; hasCoords: boolean } {
  let hasValidX = isValidUTMValue(cleanX, 'x');
  let hasValidY = isValidUTMValue(cleanY, 'y');

  // EXT-01: Buscar coordenada faltante en columnas adyacentes
  if (hasValidX && !hasValidY && structure.xColIdx >= 0) {
    const adjacent = findAdjacentCoordinate(cells, structure.xColIdx, 'y');
    if (adjacent) {
      cleanY = adjacent.value;
      hasValidY = true;
    }
  } else if (hasValidY && !hasValidX && structure.yColIdx >= 0) {
    const adjacent = findAdjacentCoordinate(cells, structure.yColIdx, 'x');
    if (adjacent) {
      cleanX = adjacent.value;
      hasValidX = true;
    }
  }

  let hasCoords = hasValidX && hasValidY;

  // Rama motor (grados/DMS/huso-29). Solo cuando el gate UTM no encontró par y la tabla lo
  // admite (allowDegreesMotor) → nunca toca el UTM nativo. Absorbe la 3.ª implementación de
  // grados (criterio 1 de Luis, A.1). Normaliza la coma decimal a punto para que la coma
  // quede inequívocamente como separador del par.
  if (!hasCoords && allowDegreesMotor) {
    const _norm = (v: string) => v.replace(',', '.');
    const _u0 = parseCoordinateToUtm30(`${_norm(cleanX)}, ${_norm(cleanY)}`);
    if (_u0) {
      cleanX = String(_u0.x);
      cleanY = String(_u0.y);
      hasCoords = true;
    }
    // Fallback: escanear columnas adyacentes buscando par lon/lat
    if (!hasCoords && structure.xColIdx >= 0) {
      for (let _dc = 1; _dc <= 3 && !hasCoords; _dc++) {
        const _cxIdx = structure.xColIdx + _dc;
        const _cyIdx = structure.yColIdx >= 0 ? structure.yColIdx + _dc : _cxIdx + 1;
        const _cx = cleanCoordinateValue(cells[_cxIdx] ?? '');
        const _cy = cleanCoordinateValue(cells[_cyIdx] ?? '');
        const _u = parseCoordinateToUtm30(`${_norm(_cx)}, ${_norm(_cy)}`);
        if (_u) {
          cleanX = String(_u.x);
          cleanY = String(_u.y);
          hasCoords = true;
        }
      }
    }
  }

  return { cleanX, cleanY, hasCoords };
}

/**
 * Extrae infraestructuras de un documento ODT usando estrategia escalonada.
 * 
 * E2: Integración detector ED50 - Analiza texto y metadatos para detectar
 * posible uso de datum ED50 (pre-2007) en el documento.
 */
export async function extractFromODT(
  file: File,
  municipality?: string,
  province?: string
): Promise<{ infrastructures: ExtractedInfrastructure[], stats: ExtractionStats, ed50Detection?: ED50Detection, detectedMunicipality?: string, detectedProvince?: string }> {
  
  const buffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buffer);
  
  const contentXml = await zip.file('content.xml')?.async('text');
  if (!contentXml) {
    throw new Error('No se pudo leer el contenido del ODT');
  }
  
  // E2: Detección de datum ED50
  // Pre-filtro rápido para evitar análisis innecesario
  let ed50Detection: ED50Detection | undefined;
  const documentText = extractTextForED50Analysis(contentXml);
  
  if (tieneIndiciosED50(documentText)) {
    // Tiene indicios, hacer análisis completo
    const documentDate = await extractODTDate(zip);
    const ed50Result = detectarED50(documentText, documentDate ?? undefined);
    ed50Detection = mapED50ResultToDetection(ed50Result);
  } else {
    // Sin indicios en texto, verificar solo por fecha
    const documentDate = await extractODTDate(zip);
    if (documentDate) {
      const ed50Result = detectarED50(undefined, documentDate);
      if (ed50Result.posibleED50) {
        ed50Detection = mapED50ResultToDetection(ed50Result);
      }
    }
  }
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(contentXml, 'text/xml');
  
  const tableNS = 'urn:oasis:names:tc:opendocument:xmlns:table:1.0';
  const tables = doc.getElementsByTagNameNS(tableNS, 'table');
  
  // Detección de municipio (movida antes del check de tablas para M02 fallback)
  const municipalityFromFilename = detectMunicipality(file.name);
  const municipalityFromContent = extractMunicipalityFromContent(contentXml);
  
  let detectedMunicipality = municipality;
  if (!detectedMunicipality && municipalityFromContent) {
    const validation = validateMunicipality(municipalityFromContent);
    if (validation.isValid) {
      detectedMunicipality = municipalityFromContent;
      console.log(`[Extractor] Municipio desde contenido: "${municipalityFromContent}"`);
    }
  }
  if (!detectedMunicipality && municipalityFromFilename) {
    const validation = validateMunicipality(municipalityFromFilename);
    if (validation.isValid) {
      detectedMunicipality = municipalityFromFilename;
    } else {
      console.warn(`[Extractor] Municipio inválido del filename: "${municipalityFromFilename}" - ${validation.reason}`);
      if (municipalityFromContent) {
        detectedMunicipality = municipalityFromContent;
      }
    }
  }
  
  // VALMUN (DOP-1 a, DOP-2 b+a · selladas por Luis 19-sep-2026): el `municipio`
  // que se sella en cada FILA exige lo mismo que la cabecera de `CABMUN`: resolver
  // a INE. `validateMunicipality` es lista negra y daba por buenos «Modelo» o
  // «Cortes de Graena», que acababan en la fila y, desde ahí, en la consulta de
  // geocodificación (`useAutoGeocoding`) y en el XLSX. Orden: parámetro >
  // contenido con INE > nombre de fichero con INE; si ninguno resuelve, la fila
  // queda sin municipio (el gate de P1 ya lo pide). `detectedMunicipality` se
  // sigue devolviendo tal cual para la cabecera, que conserva el mejor nombre
  // sin INE y así el gate propone candidatos.
  const rowMunicipality = municipality
    || [municipalityFromContent, municipalityFromFilename].find(m => !!m && !!getCodigoINE(m))
    || undefined;
  const detectedProvince = province || detectProvince(rowMunicipality || detectedMunicipality);
  
  if (tables.length === 0) {
    // M02: Fallback a texto libre si no hay tablas
    const fallback = extractFromODTFreeText(contentXml, rowMunicipality, detectedProvince);
    if (fallback.infrastructures.length > 0) {
      return {
        infrastructures: fallback.infrastructures,
        stats: fallback.stats,
        ...(ed50Detection && { ed50Detection }),
        ...(detectedMunicipality && { detectedMunicipality }),
        ...(detectedProvince && { detectedProvince }),
      };
    }
    throw new Error('No se encontraron tablas ni coordenadas en texto libre del documento ODT');
  }
  
  const infrastructures: ExtractedInfrastructure[] = [];
  const byTypology: Record<string, number> = {};
  const byTable: Record<string, number> = {};
  let withCoordinates = 0;
  let withoutCoordinates = 0;
  // INFADM-S2: balance de ingesta por tabla (candidatas vs ingeridas vs placeholders).
  const balancePorTabla = new Map<string, TableIngestionBalance>();
  
  // Municipio y provincia ya detectados antes del check de tablas (M02 fallback)
  
  // Set para rastrear tablas ya procesadas (evitar duplicados con anidadas)
  const processedTables = new Set<Element>();
  
  // Cola de tablas a procesar (incluye tablas de nivel superior y anidadas)
  interface TableToProcess {
    table: Element;
    tableName: string;
    sectionContext?: string;
    isNested: boolean;
  }
  const tablesToProcess: TableToProcess[] = [];
  
  // EXT-04: Primero, identificar tablas de nivel superior (no anidadas)
  for (let t = 0; t < tables.length; t++) {
    const table = tables[t];
    if (!isNestedTable(table, tableNS)) {
      const tableName = table.getAttribute('table:name') || `Tabla${t + 1}`;
      tablesToProcess.push({ table, tableName, isNested: false });
    }
  }
  
  // Procesar cada tabla (incluyendo anidadas que se descubran)
  while (tablesToProcess.length > 0) {
    const { table, tableName, sectionContext: _sectionContext, isNested: _isNested } = tablesToProcess.shift()!;
    
    // Evitar procesar la misma tabla dos veces
    if (processedTables.has(table)) continue;
    processedTables.add(table);

    // EXT-04 + TABGEM (DOP-1 a, DOP-2 a · selladas por Luis 28-sep-2026): una tabla con
    // tablas anidadas es un envoltorio (título, prosa o una celda que las contiene). Sus
    // filas no se leen: `getElementsByTagNameNS('table-row')` es recursivo y la contenedora
    // releía, aplanadas y desalineadas, las filas de su anidada, que después se leía otra vez.
    // Medido (`.ptel/p0_tabgem_20260928/`): 323 contenedoras en 14 ficheros, 0 con dato propio;
    // se van 233 filas de 5 ficheros (las 55 con UTM tienen su gemela a ≤ 1 m) y el resto no
    // cambia. Cada anidada se encola siempre y se juzga por sus propias filas.
    const nestedTables = findNestedTables(table, tableNS, tableName);
    if (nestedTables.length > 0) {
      for (const nested of nestedTables) {
        // Construir nombre compuesto para tabla anidada
        const nestedName = nested.sectionContext
          ? `${tableName} > ${nested.sectionContext}`
          : `${tableName} > Subtabla`;

        tablesToProcess.push({
          table: nested.table,
          tableName: nestedName,
          sectionContext: nested.sectionContext,
          isNested: true
        });
      }
      continue;
    }
    
    const tableRows = table.getElementsByTagNameNS(tableNS, 'table-row');
    
    if (tableRows.length < 2) continue;
    
    // BUG-PARSE-001: Convertir filas a arrays de celdas CON expansión multirow
    // Algunas tablas PTEL (ej: Colomera) usan formato "pseudo-multirow":
    // - Una sola fila de tabla con múltiples párrafos por celda
    // - Cada párrafo representa una infraestructura distinta
    const textNS = 'urn:oasis:names:tc:opendocument:xmlns:text:1.0';
    const allRows: string[][] = [];
    // ALBFICHA-a (DOP-1 a): la ficha de albergue del Anexo VI se lee de los párrafos de cada
    // celda ANTES de trocear la fila y se cuelga de la fila que se queda (la primera); el
    // troceo no cambia. Paralelo a `allRows` (misma longitud; NOMTIT y MJ-08 son 1:1).
    const fichasPorFila: Array<FichaAlbergue | undefined> = [];
    
    for (let r = 0; r < tableRows.length; r++) {
      const cellsWithParagraphs = getRowCellsWithParagraphs(tableRows[r], tableNS, textNS);
      const ficha = extraerFichaAlbergue(cellsWithParagraphs.map(c => c.paragraphs));
      
      // Verificar si la fila necesita expansión multirow
      if (shouldExpandRow(cellsWithParagraphs)) {
        // Expandir a múltiples filas alineando párrafos por índice
        const expandedRows = expandMultiParagraphRow(cellsWithParagraphs);
        allRows.push(...expandedRows);
        expandedRows.forEach((_, i) => fichasPorFila.push(i === 0 ? ficha : undefined));
      } else {
        // Fila normal: concatenar párrafos (comportamiento legacy)
        const row = cellsWithParagraphs.map(c => c.paragraphs.join(' ').trim());
        if (row.some(cell => cell !== '')) {
          allRows.push(row);
          fichasPorFila.push(ficha);
        }
      }
    }
    
    // PASO 1: Analizar estructura de la tabla
    const structure = analyzeTableStructure(allRows);

    // MJ-08/MJ-10: Fusionar fragmentos de nombre en columnas adyacentes
    // Corrige "Médico de" | "Cabecera" → "Médico de Cabecera" y cadenas de >2 celdas
    // NOMTIT: el autor cambió «Nombre» y «Entidad titular» → se intercambian antes de leer el nombre
    const { rows: filasNomTit } = intercambiarNombreTitular(allRows, structure);
    const mergedRows = filasNomTit.map(row =>
      mergeAdjacentCellFragments(row, structure.nameColIdx)
    );

    // Solo procesar tablas con estructura de infraestructura (confianza >= 50)
    // O que contengan coordenadas UTM válidas
    // Fix B-bis: ampliar hasUTMCoords para WGS84 *solo* cuando la sub-cabecera
    // contiene LONGITUD/LATITUD (guard anti-falso-positivo: evita activar tablas
    // con valores 35-39 por razones no geodésicas — superficies, edades, km...).
    // Refuerzo: se busca en filas 0-3 (sub-cabecera), ampliado en Fix L3-PRE
    // para cubrir variantes de plantilla donde el rowspan empuja la sub-cabecera
    // a fila 0 o fila 2 (Castril Tabla62/63/64: fila2="Longitud|Latitud").
    // Guard longitud < 60 chars: distingue sub-cabecera corta de texto descriptivo.
    const hasLonLatSubcab = allRows.slice(0, 4).some(row => {
      const text = row.join(' ').toUpperCase();
      if (text.length >= 60) return false;
      return /LONGITUD/.test(text) && /LATITUD/.test(text);
    });
    const hasUTMCoords = allRows.some(row =>
      row.some(cell => {
        const cleaned = cleanCoordinateValue(cell);
        if (isValidUTMValue(cleaned, 'y')) return true;
        // Fix B-bis: WGS84 lat/lon solo si sub-cab confirma que son coordenadas
        if (hasLonLatSubcab) {
          const num = parseFloat(cleaned);
          if (!isNaN(num) && ((num >= 35.5 && num <= 38.8) || (num >= -7.5 && num <= -1.5))) {
            return true;
          }
        }
        return false;
      })
    );
    
    // Sprint 1: Filtrar tablas de hidrografía (ríos, arroyos) - falsos positivos
    const headerRow = allRows[0] || [];
    const firstColumnValues = allRows.slice(structure.dataStartRow, structure.dataStartRow + 5)
      .map(row => row[0] || '')
      .filter(v => v.trim());
    
    if (isHydrographyTableValidator(headerRow, firstColumnValues)) {
      console.log(`[Extractor] Tabla "${tableName}" excluida: tabla de hidrografía`);
      continue;
    }
    
    // Filtrar tablas que son claramente de personal/contactos
    const tableText = allRows.slice(0, 5).flat().join(' ').toLowerCase();
    const isPersonnelTable = /cargo|tel[eé]fono\s*(de)?\s*contacto|disponibilidad|suplente|titular|responsable/.test(tableText)
      && !/coordenada|utm|direcci[oó]n|ubicaci[oó]n/.test(tableText);
    
    if (isPersonnelTable) continue;
    
    // Sprint 1: Excluir tablas de hidrografía (ríos, arroyos) que contienen longitudes, NO coordenadas
    const tableHeaders = allRows[0] || [];
    const firstColValues = allRows.slice(1, 6).map(row => row[0] || '');
    if (isHydrographyTableValidator(tableHeaders, firstColValues)) {
      console.log(`[Extractor] Tabla "${tableName}" excluida: detectada como hidrografía`);
      continue;
    }
    
    // Requerir confianza mínima de 50 (Nombre+Dirección o Nombre+Coordenadas)
    // O presencia de coordenadas UTM válidas
    if (structure.confidence < 50 && !hasUTMCoords) continue;
    
    // FIX v3.9: Validar que las columnas detectadas como coordenadas realmente contengan datos UTM
    // Esto filtra tablas como Tabla4 de Guadix (longitudes hidrográficas) que tienen headers engañosos
    // Fix L3-PRE: eximir tablas con hasLonLatSubcab=true (coords WGS84 válidas, no UTM)
    if (structure.hasCoordColumns && !hasLonLatSubcab && !tableHasValidUTMData(allRows, structure.xColIdx, structure.yColIdx, structure.dataStartRow)) {
      // La tabla tiene columnas marcadas como coordenadas pero sin datos UTM válidos
      // Solo continuar si tiene otras columnas de estructura (Nombre+Dirección) con confianza suficiente
      if (structure.confidence < 60) continue;
      // Resetear flags de coordenadas para no intentar extraerlas
      structure.hasCoordColumns = false;
      structure.xColIdx = -1;
      structure.yColIdx = -1;
    }
    
    // PASO 2: Si no detectamos estructura pero hay coords, buscar por valores
    if (!structure.hasCoordColumns && hasUTMCoords) {
      // Buscar columnas por valores numéricos
      for (let r = structure.dataStartRow; r < Math.min(allRows.length, structure.dataStartRow + 3); r++) {
        const row = allRows[r];
        for (let c = 0; c < row.length; c++) {
          const cleaned = cleanCoordinateValue(row[c]);
          if (isValidUTMValue(cleaned, 'x') && structure.xColIdx === -1) {
            structure.xColIdx = c;
          } else if (isValidUTMValue(cleaned, 'y') && structure.yColIdx === -1) {
            structure.yColIdx = c;
          }
        }
        if (structure.xColIdx !== -1 && structure.yColIdx !== -1) break;
      }
      structure.hasCoordColumns = structure.xColIdx !== -1 || structure.yColIdx !== -1;
    }
    
    // PASO 3: Extraer infraestructuras
    for (let r = structure.dataStartRow; r < mergedRows.length; r++) {
      const cells = mergedRows[r];

      // INFADM-S2: contabilizar fila candidata (nombre plausible ≥3) para el balance
      // de ingesta, ANTES del filtro de validez → toda pérdida queda visible.
      const _nameIdx = structure.nameColIdx >= 0 ? structure.nameColIdx : 0;
      const _candName = (cells[_nameIdx] ?? '').trim();
      if (_candName.length >= 3) {
        const _b = balancePorTabla.get(tableName)
          ?? { tabla: tableName, filasCandidatas: 0, filasIngeridas: 0, filasDescartadas: 0, placeholders: 0 };
        _b.filasCandidatas++;
        balancePorTabla.set(tableName, _b);
      }

      if (!isValidInfrastructureRow(cells, structure)) continue;
      
      // Extraer campos con desconcatenación
      const nombreRaw = (cells[structure.nameColIdx] || cells[0] || '').trim();
      // F307 (INFADM-S2): ¿el nombre es una plantilla «pendiente de rellenar»
      // embebida («_____Indicar_____»)? Se marca (no se borra) y se excluye de
      // la geocodificación aguas abajo (needsGeocoding).
      const esPlaceholderRow = esNombrePlaceholder(nombreRaw);
      const direccionRaw = structure.addressColIdx >= 0 ? (cells[structure.addressColIdx] || '').trim() : '';
      const tipoRaw = structure.typeColIdx >= 0 ? (cells[structure.typeColIdx] || '').trim() : '';
      
      // M01: normalizeSpanFragments ANTES de deconcatenateText (repara "d e" → "de")
      const nombre = deconcatenateText(normalizeSpanFragments(nombreRaw).corrected).corrected;
      // DIRCAJA: la dirección pasa a caja RAE en el origen (P1-P4 y exportaciones leen este campo).
      const direccionDoc = deconcatenateText(normalizeSpanFragments(direccionRaw).corrected).corrected;
      const direccion = aCajaDireccion(direccionDoc);
      const tipo = deconcatenateText(normalizeSpanFragments(tipoRaw).corrected).corrected;
      
      // Coordenadas
      let rawX = structure.xColIdx >= 0 && structure.xColIdx < cells.length ? cells[structure.xColIdx] : '';
      let rawY = structure.yColIdx >= 0 && structure.yColIdx < cells.length ? cells[structure.yColIdx] : '';
      
      // EXT-05: Detectar coordenadas concatenadas en misma celda
      // Si X tiene valor pero Y está vacío, o hay celda de coordenadas combinada
      if (rawX && !rawY) {
        const concatenated = splitConcatenatedCoordinates(rawX);
        if (concatenated) {
          rawX = concatenated.x;
          rawY = concatenated.y;
        }
      } else if (rawY && !rawX) {
        const concatenated = splitConcatenatedCoordinates(rawY);
        if (concatenated) {
          rawX = concatenated.x;
          rawY = concatenated.y;
        }
      }
      
      // FORK-A A.2 (α): detector ÚNICO de coordenada (gate UTM nativo + EXT-01 + rama motor
      // grados/DMS/huso-29 gated por hasLonLatSubcab). Reemplaza el gate/motor inline de A.1;
      // la deriva extracción↔edición queda cerrada en un solo punto de decisión (mismo helper
      // que la ruta de hoja de cálculo). cleanX/cleanY/hasCoords siguen mutables: EXT-07 e
      // IEP-02 (abajo) los reasignan si el detector no encontró par.
      const _det = detectRowCoordinate(
        cells,
        structure,
        cleanCoordinateValue(rawX),
        cleanCoordinateValue(rawY),
        hasLonLatSubcab,
      );
      let cleanX = _det.cleanX;
      let cleanY = _det.cleanY;
      let hasCoords = _det.hasCoords;

      // EXT-07: Coordenadas embebidas en celda de nombre (tablas tipo AFORO/ficha albergue)
      // Caso: "Pabellón polideportivo municipal AFORO:130 ... Paraje X  593810  4114720"
      // El nombre, metadata y coords están concatenados en una sola celda.
      // Condición: sin coords válidas, celda de nombre suficientemente larga (>50 chars).
      if (!hasCoords && nombreRaw.length > 50) {
        const embedded = extractCoordinatesFromField(nombreRaw, 'nombre');
        if (embedded) {
          cleanX = String(embedded.x);
          cleanY = String(embedded.y);
          hasCoords = true;
        }
      }

      // IEP-02: Escaneo de coords en celdas no-coord de la misma fila.
      // Técnicos municipales a veces rellenan las coords en la columna de dirección,
      // observaciones o tipo en lugar de las columnas X/Y correspondientes.
      // Orden: direccion → freeText (observaciones/notas) → tipo
      // Solo se activa cuando !hasCoords tras todos los intentos anteriores.
      if (!hasCoords) {
        // IEP-02a: Celda de dirección (el caso más frecuente)
        if (direccionRaw.length > 5) {
          const fromDireccion = extractCoordinatesFromField(direccionRaw, 'direccion');
          if (fromDireccion) {
            cleanX = String(fromDireccion.x);
            cleanY = String(fromDireccion.y);
            hasCoords = true;
          }
        }

        // IEP-02b: Columna de texto libre detectada (observaciones, notas, descripción)
        if (!hasCoords && structure.freeTextColIdx >= 0 && structure.freeTextColIdx < cells.length) {
          const freeTextRaw = cells[structure.freeTextColIdx]?.trim() ?? '';
          if (freeTextRaw.length > 5) {
            const fromFreeText = extractCoordinatesFromField(freeTextRaw, 'freeText');
            if (fromFreeText) {
              cleanX = String(fromFreeText.x);
              cleanY = String(fromFreeText.y);
              hasCoords = true;
            }
          }
        }

        // IEP-02c: Celda de tipo (poco frecuente, último recurso antes de geocodificar)
        if (!hasCoords && tipoRaw.length > 5) {
          const fromTipo = extractCoordinatesFromField(tipoRaw, 'tipo');
          if (fromTipo) {
            cleanX = String(fromTipo.x);
            cleanY = String(fromTipo.y);
            hasCoords = true;
          }
        }
      }

      // Clasificar tipo de dirección para geocodificación
      const addressType = classifyAddressType(direccion, nombre);

      // IEP-08: Extraer referencia catastral de cualquier celda de la fila
      const catRef = extractCadastralRefFromRow(cells);
      // Si hay refcat y aún no es cadastral, promover addressType
      const addressTypeFinal: AddressType =
        (catRef && addressType === 'unknown') ? 'cadastral' : addressType;
      
      // IEP-03: Extraer campos de contacto y dirección alternativa
      const obsRaw = structure.freeTextColIdx >= 0 && structure.freeTextColIdx < cells.length
        ? (cells[structure.freeTextColIdx]?.trim() ?? '')
        : '';
      const telRaw = structure.telefonoColIdx >= 0 && structure.telefonoColIdx < cells.length
        ? (cells[structure.telefonoColIdx]?.trim() ?? '')
        : '';
      const emailRaw = structure.emailColIdx >= 0 && structure.emailColIdx < cells.length
        ? (cells[structure.emailColIdx]?.trim() ?? '')
        : '';
      const direccionAltVal = extractDireccionAlt(obsRaw) || undefined;

      // Detectar tipo de infraestructura
      const detectedType = tipo || detectInfrastructureType(nombre, tipo, tableName);

      // EXT-07b: Si el nombre contiene metadatos AFORO embebidos, truncar antes del bloque
      // "Pabellón polideportivo municipal AFORO : 130 ..." → "Pabellón polideportivo municipal"
      // NOMRAE-2: caja «frase con nombres propios» (RAE) justo antes de recortar.
      // AFOROP1: el bloque recortado ya no se pierde — el aforo se captura (DOP-1 b′).
      const recorte = recortarAforoDelNombre(nombre, nombreRaw, rowMunicipality);
      const nombreClean = recorte.nombreClean;
      
      const infrastructure: ExtractedInfrastructure = {
        id: generateId(),
        tabla: tableName,
        nombre: nombreClean.substring(0, 150),
        // UI-03: Preservar nombre original solo si difiere del normalizado
        // NOMRAE-2 (DOP-6): la caja sola no cuenta como diferencia.
        // AFOROP1 (DOP-2 a): el aforo capturado tampoco — se compara sin el bloque AFORO.
        nombreOriginal: difierenSinCaja(recorte.nombreRawSinAforo, recorte.nombreComparable) ? nombreRaw.substring(0, 150) : undefined,
        aforoDeclarado: recorte.aforoDeclarado,
        aforoUnidad: recorte.aforoUnidad,
        aforoFuente: recorte.aforoFuente,
        aforoConfianza: recorte.aforoConfianza,
        tipo: detectedType,
        direccion: direccion.substring(0, 200),
        // DIRCAJA: la dirección tal como venía, solo si la caja la ha cambiado (la leerá la exportación).
        direccionOriginal: direccion !== direccionDoc ? direccionDoc.substring(0, 200) : undefined,
        xOriginal: cleanX,
        yOriginal: cleanY,
        hasCoordinates: hasCoords,
        status: hasCoords ? 'original' : 'pending',
        municipio: rowMunicipality,
        provincia: detectedProvince,
        addressType: addressTypeFinal,
        // IEP-03: Campos de contacto y dirección alternativa
        telefono:      telRaw || undefined,
        email:         emailRaw || undefined,
        observaciones: obsRaw || undefined,
        direccionAlt:  direccionAltVal,
        // IEP-08: Referencia catastral extraída de texto libre
        refCatastral:      catRef?.refCatastral,
        poligonoCatastral: catRef?.poligono,
        parcelaCatastral:  catRef?.parcela,
        // F307 (INFADM-S2): marca de plantilla «pendiente de rellenar».
        ...(esPlaceholderRow && { esPlaceholder: true }),
        // ALBFICHA-a: ficha de albergue del plan (Anexo VI), si la fila la trae rellena.
        ...(fichasPorFila[r] && { fichaAlbergue: fichasPorFila[r] }),
      };
      
      infrastructures.push(infrastructure);

      // INFADM-S2: fila efectivamente ingerida (y, si procede, plantilla).
      {
        const _b = balancePorTabla.get(tableName)
          ?? { tabla: tableName, filasCandidatas: 0, filasIngeridas: 0, filasDescartadas: 0, placeholders: 0 };
        _b.filasIngeridas++;
        if (esPlaceholderRow) _b.placeholders++;
        balancePorTabla.set(tableName, _b);
      }
      
      // Estadísticas
      byTypology[detectedType] = (byTypology[detectedType] || 0) + 1;
      byTable[tableName] = (byTable[tableName] || 0) + 1;
      
      if (hasCoords) {
        withCoordinates++;
      } else {
        withoutCoordinates++;
      }
    }
    
    // EXT-04: las tablas anidadas se encolan al principio del bucle (TABGEM).
  }
  
  // M02: Si tablas no produjeron resultados, intentar fallback texto libre
  if (infrastructures.length === 0) {
    const fallback = extractFromODTFreeText(contentXml, rowMunicipality, detectedProvince);
    if (fallback.infrastructures.length > 0) {
      return {
        infrastructures: fallback.infrastructures,
        stats: fallback.stats,
        ...(ed50Detection && { ed50Detection }),
        ...(detectedMunicipality && { detectedMunicipality }),
        ...(detectedProvince && { detectedProvince }),
      };
    }
  }
  
  // INFADM-S2: consolidar el balance de ingesta (descartadas = candidatas − ingeridas).
  const porTabla: TableIngestionBalance[] = [];
  for (const b of balancePorTabla.values()) {
    b.filasDescartadas = b.filasCandidatas - b.filasIngeridas;
    porTabla.push(b);
  }
  const balance: IngestionBalance = {
    porTabla,
    totalCandidatas: porTabla.reduce((s, b) => s + b.filasCandidatas, 0),
    totalIngeridas: porTabla.reduce((s, b) => s + b.filasIngeridas, 0),
    totalDescartadas: porTabla.reduce((s, b) => s + b.filasDescartadas, 0),
    totalPlaceholders: porTabla.reduce((s, b) => s + b.placeholders, 0),
  };

  return {
    infrastructures,
    stats: {
      total: infrastructures.length,
      withCoordinates,
      withoutCoordinates,
      byTypology,
      byTable,
      balance,
    },
    // E2: Incluir detección ED50 solo si se detectó algo
    ...(ed50Detection && { ed50Detection }),
    // CABMUN: el camino normal (con tablas) también devuelve el municipio que
    // sella en las filas; antes solo lo hacían los dos de texto libre.
    ...(detectedMunicipality && { detectedMunicipality }),
  };
}

/**
 * MTAB · «anclar la cabecera, no partir la hoja».
 *
 * El libro-formulario `.xls` del PTEL apila 26-45 tablas por hoja separadas por
 * marcadores `[INICIO_TABLA]` / `[FIN_TABLA]`. `analyzeTableStructure` solo busca
 * cabecera en las primeras filas del rango que recibe, así que analizar la hoja
 * entera encuentra la primera tabla y pierde las 25-44 restantes
 * (`OBS-P1-XLS-CERO-COORDENADAS-SILENCIOSO`, causa en `AUDIT_EMBU4.md` §3.3).
 *
 * NO se segmenta la hoja en bloques: se recorren los marcadores como *candidatos
 * a cabecera* y solo cualifica el que va seguido de una cabecera de coordenadas.
 * Consecuencias medidas en `AUDIT_MTAB.md` §4:
 *  - las tablas sin coordenadas no se tocan → `S1a` (sobre-segmentación, Guadix)
 *    queda fuera del alcance por construcción, no por prudencia;
 *  - un marcador que comparte fila con datos (§3.2) no cualifica, y no pierde
 *    su primera fila, porque la hoja íntegra sigue siendo el rango por defecto;
 *  - los `.ods` tienen cero marcadores → devuelve `[rows]` y su ruta no cambia.
 *
 * @returns rangos a analizar. Si ningún marcador cualifica, la hoja íntegra.
 */
function segmentSheetByCoordMarkers(rows: string[][]): string[][][] {
  const isMarker = (cells: string[], token: string) =>
    cells.some(c => c.toUpperCase().includes(token));

  const markerRows: number[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (isMarker(rows[i], '[INICIO_TABLA]')) markerRows.push(i);
  }
  if (markerRows.length === 0) return [rows];

  const segments: string[][][] = [];
  for (const m of markerRows) {
    const header = rows[m + 1];
    if (!header) continue;

    // Cualifica solo si la fila siguiente al marcador es cabecera de coordenadas.
    const joined = header.join(' ').toUpperCase();
    if (!joined.includes('COORDENADA X') || !joined.includes('COORDENADA Y')) continue;

    // Fin: el primer cierre o apertura posterior a la cabecera, o el fin de hoja.
    let end = rows.length;
    for (let j = m + 2; j < rows.length; j++) {
      if (isMarker(rows[j], '[FIN_TABLA]') || isMarker(rows[j], '[INICIO_TABLA]')) {
        end = j;
        break;
      }
    }
    // MTAB §4: bajo la cabecera legible va una fila de identificadores técnicos
    // (IT_ARSI_TBL_…) que no es un registro. Se excluye del rango; si entrara,
    // saldría como infraestructura con el identificador metido en X/Y.
    const bloque = rows.slice(m + 1, end);
    const esFilaIT = (cells: string[] | undefined) =>
      !!cells && cells.some(v => /^IT_[A-Z0-9_]+$/.test(v.trim()));
    if (esFilaIT(bloque[1])) bloque.splice(1, 1);

    segments.push(bloque);
  }

  // Ningún marcador cualificó → comportamiento previo intacto.
  return segments.length > 0 ? segments : [rows];
}

export async function extractFromSpreadsheet(
  file: File,
  municipality?: string,
  province?: string
): Promise<{ infrastructures: ExtractedInfrastructure[], stats: ExtractionStats, detectedMunicipality?: string }> {
  
  const XLSX = await getSheetJS();
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // TAB-FIX-S1: SheetJS lee xlsx/xls(BIFF8)/ods/csv por deteccion de contenedor.
  //
  // CSVE (`OBS-P1-CSV-LATIN1-CORROMPE-Y-PIERDE-COLUMNA`): xlsx/xls/ods son
  // contenedores binarios que declaran su propia codificación, pero un `.csv` son
  // bytes desnudos y SheetJS los decodifica como Latin-1. Eso rompe los acentos
  // («Guardia Civil de VÃ­znar») y, peor, alcanza a la cabecera («DirecciÃ³n»):
  // `analyzeTableStructure` deja de reconocer la columna y la dirección se pierde
  // entera, sin aviso y con los recuentos correctos. Por eso el `.csv` se decodifica
  // como UTF-8 y se entrega ya como texto.
  //
  // Solo para `.csv`: decodificar a texto los bytes de un contenedor ZIP (`.ods`,
  // `.xlsx`) lanza «Bad compressed size» — medido en la sonda de la sesión.
  const esCSV = /\.csv$/i.test(file.name);
  const workbook = esCSV
    ? XLSX.read(new TextDecoder('utf-8').decode(bytes), { type: 'string' })
    : XLSX.read(bytes, { type: 'array' });

  const infrastructures: ExtractedInfrastructure[] = [];
  const byTypology: Record<string, number> = {};
  const byTable: Record<string, number> = {};
  let withCoordinates = 0;
  let withoutCoordinates = 0;

  // HOJAMUN (DOP-1 a, DOP-2 b, DOP-5 a · selladas por Luis 19-sep-2026): los
  // nombres de hoja de cálculo del corpus son `YYMMDD_<TIPOLOGÍA>_<MUNICIPIO>` o
  // `plantilla_<MUNICIPIO>` y ningún patrón de `detectMunicipality` los cubre
  // (medido en `.ptel/p0_hojamun_20260919/`: 11 de 11 sin cabecera). Misma vara
  // que `CABMUN`: un candidato solo gana si resuelve a INE. El contenido no se
  // mira: solo 1 de 11 hojas nombra el municipio y a esa ya la resuelve el nombre.
  const municipalityFromPatterns = detectMunicipality(file.name);
  const detectedMunicipality = municipality
    || [municipalityFromPatterns, detectMunicipalityFromNameSuffix(file.name)].find(m => !!m && !!getCodigoINE(m))
    || municipalityFromPatterns;
  const detectedProvince = province || detectProvince(detectedMunicipality);

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;

    // TAB-FIX-S1: `defval: ''` devuelve filas DENSAS — sin huecos. Esto elimina en
    // origen la fila dispersa que hacia estallar analyzeTableStructure con ExcelJS
    // (row.values es disperso y .slice(1)+.map conservaba los huecos).
    const data = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      raw: false,
      defval: '',
    });

    if (data.length < 2) continue;

    // Convertir a strings
    const rows: string[][] = data.map(row =>
      (row || []).map(cell => String(cell ?? '').trim())
    );
    
    // MTAB: una hoja puede apilar varias tablas. Sin marcadores → la hoja íntegra.
    for (const seg of segmentSheetByCoordMarkers(rows)) {
      const structure = analyzeTableStructure(seg);
      // NOMTIT: el autor cambió «Nombre» y «Titular» → se intercambian antes de leer el nombre
      const { rows: segNomTit } = intercambiarNombreTitular(seg, structure);
    
      // Procesar filas
      for (let r = structure.dataStartRow; r < segNomTit.length; r++) {
        const cells = segNomTit[r];
        if (!isValidInfrastructureRow(cells, structure)) continue;
      
        // Extraer campos con desconcatenación (igual que ODT)
        const nombreRaw = (cells[structure.nameColIdx] || cells[0] || '').trim();
        const direccionRaw = structure.addressColIdx >= 0 ? (cells[structure.addressColIdx] || '').trim() : '';
        const tipoRaw = structure.typeColIdx >= 0 ? (cells[structure.typeColIdx] || '').trim() : '';
      
        // M01: normalizeSpanFragments ANTES de deconcatenateText (repara "d e" → "de")
        const nombre = deconcatenateText(normalizeSpanFragments(nombreRaw).corrected).corrected;
        // DIRCAJA: misma caja que en la ruta ODT.
        const direccionDoc = deconcatenateText(normalizeSpanFragments(direccionRaw).corrected).corrected;
        const direccion = aCajaDireccion(direccionDoc);
        const tipo = deconcatenateText(normalizeSpanFragments(tipoRaw).corrected).corrected;
      
        let rawX = structure.xColIdx >= 0 ? cells[structure.xColIdx] : '';
        let rawY = structure.yColIdx >= 0 ? cells[structure.yColIdx] : '';
      
        // EXT-05: Detectar coordenadas concatenadas en misma celda
        if (rawX && !rawY) {
          const concatenated = splitConcatenatedCoordinates(rawX);
          if (concatenated) {
            rawX = concatenated.x;
            rawY = concatenated.y;
          }
        } else if (rawY && !rawX) {
          const concatenated = splitConcatenatedCoordinates(rawY);
          if (concatenated) {
            rawX = concatenated.x;
            rawY = concatenated.y;
          }
        }
      
        // FORK-A A.2 (α): mismo detector ÚNICO que la ruta ODT (convergencia de rutas).
        // allowDegreesMotor=false: la hoja de cálculo aún no detecta sub-cabecera lon/lat
        // (esa costura queda para A.3); hoy hereda gate UTM + EXT-01 vía el helper compartido.
        const _det = detectRowCoordinate(
          cells,
          structure,
          cleanCoordinateValue(rawX),
          cleanCoordinateValue(rawY),
          false,
        );
        const cleanX = _det.cleanX;
        const cleanY = _det.cleanY;
        const hasCoords = _det.hasCoords;
        const addressType = classifyAddressType(direccion, nombre);
        const detectedType = tipo || detectInfrastructureType(nombre, tipo, sheetName);

        // IEP-08: Extraer referencia catastral de cualquier celda de la fila
        const catRefXlsx = extractCadastralRefFromRow(cells);
        const addressTypeFinalXlsx: AddressType =
          (catRefXlsx && addressType === 'unknown') ? 'cadastral' : addressType;
      
        // NOMRAE-2: la ruta de hoja adopta el recorte «AFORO…» de la ruta ODT y la caja RAE.
        // AFOROP1: mismo helper que la ruta ODT — el aforo recortado se captura (DOP-1 b′).
        const recorteXlsx = recortarAforoDelNombre(nombre, nombreRaw, detectedMunicipality);
        const nombreCleanXlsx = recorteXlsx.nombreClean;

        const infraObj: ExtractedInfrastructure = {
          id: generateId(),
          tabla: sheetName,
          nombre: nombreCleanXlsx.substring(0, 150),
          // UI-03: Preservar nombre original solo si difiere del normalizado
          // NOMRAE-2 (DOP-6): la caja sola no cuenta como diferencia.
          // AFOROP1 (DOP-2 a): el aforo capturado tampoco — se compara sin el bloque AFORO.
          nombreOriginal: difierenSinCaja(recorteXlsx.nombreRawSinAforo, recorteXlsx.nombreComparable) ? nombreRaw.substring(0, 150) : undefined,
          aforoDeclarado: recorteXlsx.aforoDeclarado,
          aforoUnidad: recorteXlsx.aforoUnidad,
          aforoFuente: recorteXlsx.aforoFuente,
          aforoConfianza: recorteXlsx.aforoConfianza,
          tipo: detectedType,
          direccion: direccion.substring(0, 200),
          direccionOriginal: direccion !== direccionDoc ? direccionDoc.substring(0, 200) : undefined,
          xOriginal: cleanX,
          yOriginal: cleanY,
          hasCoordinates: hasCoords,
          status: hasCoords ? 'original' : 'pending',
          municipio: detectedMunicipality,
          provincia: detectedProvince,
          addressType: addressTypeFinalXlsx,
          // IEP-08: Referencia catastral extraída de texto libre
          refCatastral:      catRefXlsx?.refCatastral,
          poligonoCatastral: catRefXlsx?.poligono,
          parcelaCatastral:  catRefXlsx?.parcela,
        };
      
        infrastructures.push(infraObj);
      
        byTypology[detectedType] = (byTypology[detectedType] || 0) + 1;
        byTable[sheetName] = (byTable[sheetName] || 0) + 1;
      
        if (hasCoords) withCoordinates++;
        else withoutCoordinates++;
      }
    } // MTAB: fin del recorrido de tablas de la hoja
  }
  
  return {
    infrastructures,
    stats: {
      total: infrastructures.length,
      withCoordinates,
      withoutCoordinates,
      byTypology,
      byTable
    },
    ...(detectedMunicipality && { detectedMunicipality }),
  };
}

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

export async function extractInfrastructures(
  file: File,
  municipality?: string,
  province?: string
): Promise<{ 
  infrastructures: ExtractedInfrastructure[], 
  stats: ExtractionStats,
  metadata: DocumentMetadata 
}> {
  
  const extension = file.name.split('.').pop()?.toLowerCase();
  
  // FMT-03: Establecer formato para el pipeline según extensión
  const formatMap: Record<string, DocumentFormat> = {
    'odt': 'odt',
    'xlsx': 'spreadsheet',
    'xls': 'spreadsheet',
    'ods': 'spreadsheet',
    'csv': 'spreadsheet',
  };
  setDocumentFormat(formatMap[extension || ''] || 'plain');
  
  // E2: Tipo extendido para incluir ed50Detection
  let result: { 
    infrastructures: ExtractedInfrastructure[]; 
    stats: ExtractionStats;
    ed50Detection?: ED50Detection;
    // CABMUN: municipio que la ruta ODT selló en las filas (contenido > filename)
    detectedMunicipality?: string;
  };
  
  switch (extension) {
    case 'odt':
      result = await extractFromODT(file, municipality, province);
      break;
    case 'xlsx':
    case 'xls':
    case 'ods':
    case 'csv':
      result = await extractFromSpreadsheet(file, municipality, province);
      break;
    default:
      throw new Error(`Formato no soportado: ${extension}. Use ODT, XLSX, XLS, ODS o CSV.`);
  }
  
  // CABMUN (DOP-1 a′, DOP-2 c, DOP-3 a · selladas por Luis 18-sep-2026):
  // la cabecera sigue «parámetro > contenido > filename», pero un candidato solo
  // gana si RESUELVE A INE. `validateMunicipality` no basta: es lista negra y da
  // por válido «Ficha Reducida Plantilla» o «Cortes de Graena» (medido en
  // `.ptel/p0_cabmun_20260918/`). Si ninguno resuelve, se conserva el mejor
  // nombre sin INE para que el gate de P1-S3 proponga candidatos del catálogo.
  // HOJAMUN (19-sep-2026): las hojas de cálculo también devuelven
  // `detectedMunicipality` (sufijo del nombre resuelto a INE) y entran aquí igual.
  const municipalityFromExtraction = result.detectedMunicipality;
  const municipalityFromFilename = detectMunicipality(file.name);
  const detectedMunicipality = municipality
    || [municipalityFromExtraction, municipalityFromFilename].find(m => !!m && !!getCodigoINE(m))
    || municipalityFromExtraction
    || municipalityFromFilename;
  
  return {
    infrastructures: result.infrastructures,
    stats: result.stats,
    metadata: {
      fileName: file.name,
      fileType: extension || 'unknown',
      municipality: detectedMunicipality,
      province: province || detectProvince(detectedMunicipality),
      // FIX INE-01: Asignar código INE para habilitar validación downstream
      // MJ-03: Alerta explícita cuando municipio no resuelve a INE
      ineCode: (() => {
        const ine = detectedMunicipality ? (getCodigoINE(detectedMunicipality) ?? undefined) : undefined;
        if (detectedMunicipality && !ine) {
          console.warn(
            `[Extractor] ⚠️ Municipio "${detectedMunicipality}" no resuelto a INE — verificar sufijos o variante ortográfica`
          );
        }
        return ine;
      })(),
      tablesProcessed: Object.keys(result.stats.byTable).length,
      processedAt: new Date(),
      // E2: Propagar detección ED50 a metadata
      ed50Detection: result.ed50Detection,
    }
  };
}

/**
 * HOJAMUN: municipio por sufijo del nombre de fichero, con el catálogo INE como
 * vara. Prueba los sufijos de más largo a más corto («La Guardia de Jaén» antes
 * que «Jaén») y devuelve el nombre oficial del primero que resuelve; si ninguno
 * resuelve, `undefined` (no se inventa). Solo la usa la ruta de hoja de cálculo:
 * no altera `detectMunicipality` ni la ruta ODT (DOP-5 a).
 * El nombre se pasa a NFC porque macOS entrega los nombres con tilde en NFD.
 */
export function detectMunicipalityFromNameSuffix(fileName: string): string | undefined {
  const palabras = fileName
    .normalize('NFC')
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/_/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  for (let i = 0; i < palabras.length; i++) {
    const ine = getCodigoINE(palabras.slice(i).join(' '));
    if (ine) return getMunicipioPorCodigo(ine)?.nombre;
  }
  return undefined;
}

/**
 * IEP-03: Alias de prueba para analyzeTableStructure.
 * Solo para tests — no usar en producción.
 * @internal
 */
export const detectTableStructureForTest = analyzeTableStructure;
